import React, { useEffect, useState, Fragment } from 'react';
import { ArrowLeft, Package, Barcode, CheckCircle2, AlertCircle, Truck, User, MapPin, Phone, CreditCard, Info, XCircle, ChevronRight, Scan, Clock, Globe, Smartphone, ShieldCheck } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { deriveFulfillmentProfile, Order, OrderLine, OrderStatus, PaymentType } from './OrderQueue';
import { OperationalSettings } from './SettingsPanel';
function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}
interface OrderDetailsProps {
  order: Order;
  settings: OperationalSettings;
  onBack: () => void;
  onUpdateStatus: (status: OrderStatus, updates?: Partial<Order>, systemActor?: boolean) => void;
  onUpdateLines: (lines: OrderLine[]) => void;
  onUpdatePaymentMethod?: (paymentType: PaymentType) => void;
  readOnly?: boolean;
  initialShowRejectModal?: boolean;
  initialIsBulkScanning?: boolean;
  initialConfirmItem?: OrderLine | null;
  initialShowPartialModal?: boolean;
  initialScanningItem?: OrderLine | null;
  initialShowGlobalScanModal?: boolean;
  initialShowHoldModal?: boolean;
  initialShowOtpModal?: boolean;
}
export function OrderDetails({
  order,
  settings,
  onBack,
  onUpdateStatus,
  onUpdateLines,
  initialShowRejectModal = false,
  initialIsBulkScanning = false,
  initialConfirmItem = null,
  initialShowPartialModal = false,
  initialScanningItem = null,
  initialShowGlobalScanModal = false,
  initialShowHoldModal = false,
  initialShowOtpModal = false,
  readOnly = false,
  onUpdatePaymentMethod
}: OrderDetailsProps) {
  const [isBulkScanning, setIsBulkScanning] = useState(initialIsBulkScanning);
  const [isEditingPayment, setIsEditingPayment] = useState(false);
  const [isWaitingConfirmation, setIsWaitingConfirmation] = useState(false);
  const [showAggregatorRejectionModal, setShowAggregatorRejectionModal] = useState(false);
  const [showLabelModal, setShowLabelModal] = useState(false);
  const [tempSubstitution, setTempSubstitution] = useState('');
  const items = order.lines;
  const fulfillmentProfile = deriveFulfillmentProfile(order);
  const isPaymentMethodLocked = ['Insurance', 'Partner Prepaid', 'Deferred'].includes(order.payerType);
  const [scanningItem, setScanningItem] = useState<OrderLine | null>(null);
  const [showGlobalScanModal, setShowGlobalScanModal] = useState(initialShowGlobalScanModal);
  const [globalScanValue, setGlobalScanValue] = useState('');
  const [manualQty, setManualQty] = useState('');
  const [tempSerial, setTempSerial] = useState('');
  const [tempLot, setTempLot] = useState('');
  const [showRejectModal, setShowRejectModal] = useState(initialShowRejectModal);
  const [showOrderRejectionModal, setShowOrderRejectionModal] = useState(false);
  const [orderRejectionReason, setOrderRejectionReason] = useState('');
  const [orderRejectionCustomReason, setOrderRejectionCustomReason] = useState('');
  const [showPartialModal, setShowPartialModal] = useState(initialShowPartialModal);
  const [showHoldModal, setShowHoldModal] = useState(initialShowHoldModal);
  const [showPatientShareModal, setShowPatientShareModal] = useState(false);
  const [showOtpModal, setShowOtpModal] = useState(initialShowOtpModal);
  const [showRxModal, setShowRxModal] = useState(false);
  const [showReturnModal, setShowReturnModal] = useState(false);
  const [showReturnReceiptModal, setShowReturnReceiptModal] = useState(false);
  const [returnItemStatus, setReturnItemStatus] = useState<Record<string, {
    received: boolean;
    condition: 'Good' | 'Damaged' | 'Missing';
    note: string;
    missingQuantity: number;
  }>>({});
  const [returnReason, setReturnReason] = useState('');
  const [returnMethod, setReturnMethod] = useState<'Driver' | 'Customer'>('Customer');
  const [otpValue, setOtpValue] = useState('');
  const [nationalIdValue, setNationalIdValue] = useState('');
  const [pickupMobileValue, setPickupMobileValue] = useState('');
  const [resendTimer, setResendTimer] = useState(60);
  const [canResend, setCanResend] = useState(false);
  useEffect(() => {
    let interval: any;
    if (showOtpModal && resendTimer > 0) {
      interval = setInterval(() => {
        setResendTimer(prev => prev - 1);
      }, 1000);
    } else if (resendTimer === 0) {
      setCanResend(true);
    }
    return () => clearInterval(interval);
  }, [showOtpModal, resendTimer]);
  const handleResendOtp = () => {
    setResendTimer(60);
    setCanResend(false);
    import('sonner').then(({
      toast
    }) => toast.success('OTP has been resent to the customer'));
  };
  const handleSendWhatsApp = () => {
    import('sonner').then(({
      toast
    }) => toast.success('OTP sent via WhatsApp successfully'));
  };
  const [rejectionReason, setRejectionReason] = useState('');
  const [holdReason, setHoldReason] = useState('');
  const [customReason, setCustomReason] = useState('');
  const [holdCustomReason, setHoldCustomReason] = useState('');
  const [returnCustomReason, setReturnCustomReason] = useState('');
  useEffect(() => {
    setShowRejectModal(initialShowRejectModal);
  }, [initialShowRejectModal]);
  useEffect(() => {
    setIsBulkScanning(initialIsBulkScanning);
  }, [initialIsBulkScanning]);
  useEffect(() => {
    setShowPartialModal(initialShowPartialModal);
  }, [initialShowPartialModal]);
  useEffect(() => {
    setShowGlobalScanModal(initialShowGlobalScanModal);
  }, [initialShowGlobalScanModal]);
  useEffect(() => {
    setShowHoldModal(initialShowHoldModal);
  }, [initialShowHoldModal]);
  useEffect(() => {
    setShowOtpModal(initialShowOtpModal);
  }, [initialShowOtpModal]);
  useEffect(() => {
    if (initialScanningItem) {
      setScanningItem(initialScanningItem);
      setShowGlobalScanModal(true);
      setManualQty(initialScanningItem.pickedQuantity > 0 ? initialScanningItem.pickedQuantity.toString() : initialScanningItem.quantity.toString());
    }
  }, [initialScanningItem]);
  const handleConfirmPartial = () => {
    setShowPartialModal(false);
    if (fulfillmentProfile.requiresExplicitPartialConfirmation) {
      setIsWaitingConfirmation(true);
    } else {
      onUpdateStatus(fulfillmentProfile.nextReadyStatus);
    }
  };
  const resolveCustomerPartialConfirmation = (confirmed: boolean) => {
    setIsWaitingConfirmation(false);
    if (confirmed) {
      onUpdateStatus(fulfillmentProfile.nextReadyStatus);
    } else {
      onUpdateStatus('Cancelled', {
        rejectionReason: 'Customer declined partial fulfillment'
      });
    }
  };
  const handleMarkUnavailable = (itemId: string) => {
    onUpdateLines(items.map(item => item.id === itemId ? {
      ...item,
      pickedQuantity: 0,
      selectedLot: undefined,
      selectedLotNumber: undefined,
      serialNumbers: [],
      status: 'Unavailable'
    } : item));
    setScanningItem(null);
    setShowGlobalScanModal(false);
    setTempLot('');
    setTempSerial('');
    setManualQty('');
  };
  const handleRestoreUnavailable = (itemId: string) => {
    onUpdateLines(items.map(item => item.id === itemId ? {
      ...item,
      status: 'Pending'
    } : item));
  };
  const cancelReasons = settings.cancelReasons;
  const rejectReasons = settings.rejectReasons;
  const holdReasons = settings.holdReasons;
  const returnReasons = settings.returnReasons;
  const cancelReasonRequiresText = cancelReasons.find(reason => reason.label === rejectionReason)?.requiresFreeText ?? false;
  const rejectReasonRequiresText = rejectReasons.find(reason => reason.label === orderRejectionReason)?.requiresFreeText ?? false;
  const holdReasonRequiresText = holdReasons.find(reason => reason.label === holdReason)?.requiresFreeText ?? false;
  const returnReasonRequiresText = returnReasons.find(reason => reason.label === returnReason)?.requiresFreeText ?? false;
  const handlePickItem = (itemId: string) => {
    const item = items.find(i => i.id === itemId);
    if (!item) return;
    setScanningItem(item);
    setShowGlobalScanModal(true);
    setManualQty(item.pickedQuantity > 0 ? item.pickedQuantity.toString() : item.quantity.toString());
    setTempLot(item.selectedLotNumber || '');
    setTempSubstitution(item.dispensedTradeDrugCode || '');
  };
  const handleGlobalScan = (input: string) => {
    const parts = input.split('*');
    const scanValue = parts[0].trim();
    const scanQty = parts.length > 1 ? parseInt(parts[1]) : 1;
    const item = items.find(i => i.sku === scanValue || i.isSerialized && scanValue.startsWith('SN-') || i.lotNumbers && i.lotNumbers.some(l => l.number === scanValue));
    if (item) {
      setScanningItem(item);
      setManualQty(item.pickedQuantity > 0 ? Math.min(item.pickedQuantity + scanQty, item.quantity).toString() : Math.min(scanQty, item.quantity).toString());
      if (item.isSerialized && scanValue.startsWith('SN-')) {
        setTempSerial(scanValue);
      }
      if (item.lotNumbers?.some(l => l.number === scanValue)) {
        setTempLot(scanValue);
      }
      setTempSubstitution(item.dispensedTradeDrugCode || '');
      import('sonner').then(({
        toast
      }) => {
        toast.success(`Item Identified: ${item.name}`);
      });
    } else {
      import('sonner').then(({
        toast
      }) => {
        toast.error(`Item ${scanValue} not found in this order`);
      });
    }
    setGlobalScanValue('');
  };
  const handleConfirmItemFulfillment = () => {
    if (!scanningItem) return;
    const qty = parseInt(manualQty) || 0;
    const finalQty = Math.min(qty, scanningItem.quantity);
    onUpdateLines(items.map(item => item.id === scanningItem.id ? {
      ...item,
      pickedQuantity: finalQty,
      selectedLot: tempLot,
      selectedLotNumber: tempLot,
      dispensedTradeDrugCode: tempSubstitution || item.dispensedTradeDrugCode,
      serialNumbers: tempSerial && item.isSerialized && !item.serialNumbers.includes(tempSerial) ? [...item.serialNumbers, tempSerial] : item.serialNumbers,
      status: finalQty === item.quantity ? 'Picked' : finalQty > 0 ? 'Partially Picked' : 'Pending'
    } : item));
    // If we are in bulk scan mode, we stay in the scan modal but clear the current item
    if (isBulkScanning) {
      setScanningItem(null);
      setTempSerial('');
      setTempLot('');
      setTempSubstitution('');
      setManualQty('');
      // showGlobalScanModal remains true
    } else {
      setScanningItem(null);
      setShowGlobalScanModal(false);
      setTempSerial('');
      setTempLot('');
      setTempSubstitution('');
      setManualQty('');
    }
  };
  const getTherapyEndDate = (genericCode: string) => {
    const durationDays = order.prescription?.activities.find(activity => activity.genericCode === genericCode)?.durationDays;
    if (!durationDays) return null;
    const start = new Date(order.createdAt);
    return Number.isNaN(start.getTime()) ? null : new Date(start.getTime() + durationDays * 24 * 60 * 60 * 1000);
  };
  const allPicked = items.every(item => item.status === 'Picked');
  return <div className="max-w-6xl mx-auto space-y-6 pb-20" data-id="element-26" data-mp="1vysejzbolsfe">
      {/* Header */}
      <div className="flex items-center justify-between" data-id="element-27" data-mp="1r4o3p4zap091">
        <div className="flex items-center gap-4" data-id="element-28" data-mp="1m75xhmcnwz4q">
          <button onClick={onBack} className="p-2 hover:bg-white rounded-full transition-colors border border-transparent hover:border-slate-200" data-id="element-29" data-mp="1m6mb9tfzbokw">
            <ArrowLeft className="w-5 h-5 text-slate-600" data-id="element-30" data-mp="2eurycmui9pnw" />
          </button>
          <div data-id="element-31" data-mp="cxriu42vpp85">
            <div className="flex items-center gap-3" data-id="element-32" data-mp="czaz3uojc09x">
              <h2 className="text-2xl font-bold text-slate-900" data-id="element-33" data-mp="gzlchsenpsyt">
                Order #{order.id}
              </h2>
              <div className={cn('px-2 py-0.5 rounded-md text-[10px] font-bold uppercase border', order.paymentType === 'COD' ? 'bg-amber-50 text-amber-700 border-amber-100' : 'bg-emerald-50 text-emerald-700 border-emerald-100')} data-id="element-34" data-mp="38q40h82ldvr">
                {order.paymentType}
              </div>
            </div>
            <div className="mt-0.5 flex items-center gap-2" data-id="element-35" data-mp="3gg1hza57u7t1">
              <p className="text-xs text-slate-500" data-id="element-36" data-mp="3ldb68mgvcdfa">Placed on {order.createdAt}</p>
              <span className="text-xs text-slate-300" data-id="element-37" data-mp="1endd6wcxbcb5">•</span>
              <p className="text-xs font-medium text-slate-500" data-id="element-38" data-mp="1enwohu82j2g7">Accept by {new Date(order.acceptanceDeadline).toLocaleString()}</p>
            </div>
          </div>
        </div>

        {!readOnly && <div className="flex items-center gap-3" data-id="element-39" data-mp="1tqmwsqfw161x">
            {['Pending', 'Accepted', 'Picking', 'Searching for Driver', 'Driver Assigned', 'Ready for Pickup', 'Delivery Attempt Failed'].includes(order.status) && <div className="flex items-center gap-2" data-id="element-40" data-mp="2cgwb2yoy8o9z">
                <button onClick={() => setShowRejectModal(true)} className="px-4 py-2 text-red-600 font-bold text-sm hover:bg-red-50 rounded-xl transition-colors" data-id="element-41" data-mp="2cfbdt1b92r8f">
                  Cancel Order
                </button>
                <button onClick={() => setShowHoldModal(true)} className="px-4 py-2 text-slate-600 font-bold text-sm hover:bg-slate-50 rounded-xl transition-colors" data-id="element-42" data-mp="2hh0qele4fcym">
                  Hold Order
                </button>
              </div>}

            {order.status === 'Held' && <div className="flex gap-3" data-id="element-43" data-mp="olg97c01bo6q">
                <button onClick={() => setShowRejectModal(true)} className="px-4 py-2 text-red-600 font-bold text-sm hover:bg-red-50 rounded-xl transition-colors" data-id="element-44" data-mp="thf3fg81o0f2">
                  Cancel Order
                </button>
                <button onClick={() => onUpdateStatus(order.previousStatus || 'Pending')} className="px-6 py-2 bg-blue-600 text-white font-bold rounded-xl shadow-lg shadow-blue-200 hover:bg-blue-700 transition-all" data-id="element-45" data-mp="5svvx2wtjro1">
                  Resume Order
                </button>
              </div>}

            {order.status === 'Pending' && <div className="flex items-center gap-3" data-id="element-46" data-mp="3i99acnjjsasa">
                {fulfillmentProfile.requiresPrescriptionReview && order.prescription && <button onClick={() => setShowRxModal(true)} className="px-6 py-2 bg-purple-600 text-white font-bold rounded-xl shadow-lg shadow-purple-200 hover:bg-purple-700 transition-colors flex items-center gap-2" data-id="element-47" data-mp="3fpkwsvux04bq">
                    <Globe className="w-4 h-4" data-id="element-48" data-mp="35thv1r3z67pb" />
                    Review eRx
                  </button>}
                {order.channel !== 'Aggregator' && order.channel !== 'Yaser eRx' && <button onClick={() => setShowOrderRejectionModal(true)} className="rounded-xl border border-rose-200 bg-white px-4 py-2 text-sm font-bold text-rose-700 transition-colors hover:bg-rose-50" data-id="element-49" data-mp="2s00emf6l5b3i">
                    Reject Order
                  </button>}
                <button disabled={fulfillmentProfile.requiresPrescriptionReview && !order.prescription?.verified} onClick={() => {
            if (fulfillmentProfile.requiresPatientShareCollection) {
              onUpdateStatus('Accepted');
              setShowPatientShareModal(true);
            } else if (fulfillmentProfile.requiresPaymentLink) {
              onUpdateStatus('Awaiting Payment');
            } else {
              onUpdateStatus('Accepted');
            }
          }} className="rounded-xl bg-blue-600 px-6 py-2 font-bold text-white shadow-lg shadow-blue-200 transition-colors hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50" data-id="element-50" data-mp="2dz7ijq0my7ya">
                  Accept Order
                </button>
              </div>}

            {order.status === 'Accepted' && (fulfillmentProfile.requiresPatientShareCollection ? <button onClick={() => setShowPatientShareModal(true)} className="flex items-center gap-2 rounded-xl bg-violet-600 px-6 py-2 font-bold text-white shadow-lg shadow-violet-200 transition-colors hover:bg-violet-700" data-id="element-51" data-mp="311b71gldsuda">
                  <CreditCard className="h-4 w-4" data-id="element-52" data-mp="pehhw2d6aovx" />
                  Collect Patient Share
                </button> : <button onClick={() => onUpdateStatus('Picking')} className="px-6 py-2 bg-blue-600 text-white font-bold rounded-xl shadow-lg shadow-blue-200 hover:bg-blue-700 transition-all flex items-center gap-2" data-id="element-53" data-mp="1391mvfkexbuh">
                  <Package className="w-4 h-4" data-id="element-54" data-mp="6hzpdiqi2kym" />
                  Start Picking
                </button>)}

            {order.status === 'Picking' && <>
                {allPicked ? <button onClick={() => onUpdateStatus(order.channel === 'Walk-in' ? 'Delivered' : fulfillmentProfile.nextReadyStatus)} className="flex items-center gap-2 rounded-xl bg-emerald-600 px-6 py-2 font-bold text-white shadow-lg shadow-emerald-200 transition-colors hover:bg-emerald-700" data-id="element-55" data-mp="fjh16owvk0hi">
                    <CheckCircle2 className="h-4 w-4" data-id="element-56" data-mp="2fysaexqm8hfz" />
                    {order.channel === 'Walk-in' ? 'Hand over at counter' : 'Ready to Pick'}
                  </button> : <button onClick={() => fulfillmentProfile.allowsPartialFulfillment ? setShowPartialModal(true) : setShowAggregatorRejectionModal(true)} className="flex items-center gap-2 rounded-xl bg-amber-500 px-6 py-2 font-bold text-white shadow-lg shadow-amber-200 transition-colors hover:bg-amber-600" data-id="element-57" data-mp="2or2mkkl2mwzv">
                    <AlertCircle className="h-4 w-4" data-id="element-58" data-mp="37on2311y4j7t" />
                    {fulfillmentProfile.allowsPartialFulfillment ? 'Confirm Scan' : 'Resolve unavailable item'}
                  </button>}
              </>}

            {order.status === 'Awaiting Partner Courier' && fulfillmentProfile.usesPartnerCourier && <button onClick={() => onUpdateStatus('Out for Delivery', undefined, true)} className="flex items-center gap-2 rounded-xl bg-cyan-600 px-6 py-2 font-bold text-white shadow-lg shadow-cyan-200 transition-colors hover:bg-cyan-700" data-id="element-59" data-mp="15sxqci3rlqj6">
                <Truck className="h-4 w-4" data-id="element-60" data-mp="s19d2bhs4w0m" />
                Handed to partner courier
              </button>}

            {order.status === 'Searching for Driver' && !fulfillmentProfile.usesPartnerCourier && !fulfillmentProfile.requiresPickupOtp && !fulfillmentProfile.requiresNationalIdVerification && <button onClick={() => onUpdateStatus('Driver Assigned', {
          courierPhone: order.courierName ? '+966 9200 12345' : undefined,
          driverInfo: {
            name: 'Ahmed Al-Fahad',
            phone: '+966 55 000 1111',
            vehiclePlate: 'ABC 1234',
            rating: 4.8,
            avatar: 'https://api.dicebear.com/7.x/avataaars/svg?seed=Ahmed'
          }
        })} className="flex items-center gap-2 rounded-xl bg-blue-600 px-6 py-2 font-bold text-white shadow-lg shadow-blue-200 transition-colors hover:bg-blue-700" data-id="element-61" data-mp="1fnpphqczwlg2">
                <Truck className="h-4 w-4" data-id="element-62" data-mp="3eqsr30yl5rvn" />
                Simulate Driver Assignment
              </button>}

            {order.status === 'Driver Assigned' && !fulfillmentProfile.usesPartnerCourier && <button onClick={() => onUpdateStatus('Out for Delivery')} className="px-6 py-2 bg-blue-600 text-white font-bold rounded-xl shadow-lg shadow-blue-200 hover:bg-blue-700 transition-all flex items-center gap-2" data-id="element-63" data-mp="35pl6flozspk1">
                <Truck className="w-4 h-4" data-id="element-64" data-mp="1pc4jre4ur04h" />
                Driver Picked Up
              </button>}

            {order.status === 'Out for Delivery' && <div className="flex gap-3" data-id="element-65" data-mp="1f636mndoblz4">
                <button onClick={() => onUpdateStatus('Delivery Attempt Failed', undefined, fulfillmentProfile.usesPartnerCourier)} className="rounded-xl px-4 py-2 text-sm font-bold text-rose-600 transition-colors hover:bg-rose-50" data-id="element-66" data-mp="1k6r9c6b9lr96">
                  {fulfillmentProfile.usesPartnerCourier ? 'Partner reported failed' : 'Delivery Failed'}
                </button>
                <button onClick={() => onUpdateStatus('Delivered', undefined, fulfillmentProfile.usesPartnerCourier)} className="rounded-xl bg-emerald-600 px-6 py-2 font-bold text-white shadow-lg shadow-emerald-200 transition-colors hover:bg-emerald-700" data-id="element-67" data-mp="22wqtces90g5q">
                  {fulfillmentProfile.usesPartnerCourier ? 'Partner confirmed delivery' : 'Confirm Delivery'}
                </button>
              </div>}

            {order.status === 'Delivery Attempt Failed' && <div className="flex gap-3" data-id="element-68" data-mp="ajbscrou7yy7">
                {!order.hasRetriedDelivery && <button onClick={() => onUpdateStatus('Searching for Driver', {
            hasRetriedDelivery: true
          })} className="flex items-center gap-2 rounded-xl bg-blue-600 px-6 py-2 font-bold text-white shadow-lg shadow-blue-200 transition-colors hover:bg-blue-700" data-id="element-69" data-mp="5jz7udlfv3ys">
                    <Truck className="h-4 w-4" data-id="element-70" data-mp="oh0ahrrisrzk" />
                    Retry delivery
                  </button>}
                <button onClick={() => onUpdateStatus('Held', {
            holdReason: 'Delivery failed'
          })} className="rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-bold text-slate-600 transition-colors hover:bg-slate-50" data-id="element-71" data-mp="136685bapma5c">
                  Return to branch
                </button>
                {order.hasRetriedDelivery && <span className="self-center text-[10px] font-bold uppercase tracking-wider text-slate-400" data-id="element-72" data-mp="1qsry9kawvw9f">One retry already used</span>}
              </div>}

            {order.status === 'Ready for Pickup' && (fulfillmentProfile.requiresPickupOtp || fulfillmentProfile.requiresNationalIdVerification) && <button onClick={() => setShowOtpModal(true)} className="flex items-center gap-2 rounded-xl bg-blue-600 px-6 py-2 font-bold text-white shadow-lg shadow-blue-200 transition-colors hover:bg-blue-700" data-id="element-73" data-mp="1mpvcxltzlm1s">
                <User className="h-4 w-4" data-id="element-74" data-mp="z2x0cz8zmy95" />
                {fulfillmentProfile.requiresNationalIdVerification ? 'Verify pickup identity' : 'Customer Pickup'}
              </button>}
          </div>}

        {!readOnly && <div className="flex items-center gap-3" data-id="element-75" data-mp="p4pubbg8m0lx">
            {order.channel === 'Aggregator' && order.status === 'Delivered' && <button onClick={() => onUpdateStatus('Returned to Stock')} className="flex items-center gap-2 rounded-xl bg-teal-600 px-6 py-2 font-bold text-white shadow-lg shadow-teal-200 transition-colors hover:bg-teal-700" data-id="element-76" data-mp="1wg752ku3ji6z">
                <Package className="h-4 w-4" data-id="element-77" data-mp="1nlsh7tvi2zc9" />
                Return to stock
              </button>}

            {order.channel !== 'Aggregator' && order.status === 'Delivered' && <button onClick={() => setShowReturnModal(true)} className="px-6 py-2 bg-rose-600 text-white font-bold rounded-xl shadow-lg shadow-rose-200 hover:bg-rose-700 transition-all" data-id="element-78" data-mp="1dnl27gkrurcw">
                Initiate Return
              </button>}

            {order.channel !== 'Aggregator' && order.status === 'Return Requested' && <button onClick={() => onUpdateStatus('Refunded')} className="px-6 py-2 bg-slate-800 text-white font-bold rounded-xl shadow-lg shadow-slate-200 hover:bg-slate-900 transition-all" data-id="element-79" data-mp="2z17mrm1ryziz">
                Complete Refund
              </button>}

            {order.channel !== 'Aggregator' && order.status === 'Waiting Return Confirmation' && <button onClick={() => {
          const initialStatus: Record<string, {
            received: boolean;
            condition: 'Good' | 'Damaged' | 'Missing';
            note: string;
            missingQuantity: number;
          }> = {};
          items.forEach(item => {
            initialStatus[item.id] = {
              received: true,
              condition: 'Good',
              note: '',
              missingQuantity: 0
            };
          });
          setReturnItemStatus(initialStatus);
          setShowReturnReceiptModal(true);
        }} className="px-6 py-2 bg-emerald-600 text-white font-bold rounded-xl shadow-lg shadow-emerald-200 hover:bg-emerald-700 transition-all" data-id="element-80" data-mp="3bfw11hqb9jsb">
                Confirm Item Return
              </button>}
          </div>}
      </div>

      <div className="grid grid-cols-3 gap-6" data-id="element-81" data-mp="290vehumxt0e0">
        <div className="col-span-2 space-y-6" data-id="element-82" data-mp="23ywaqjnac1gj">
          <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-sm" data-id="element-83" data-mp="242advu3rwep1">
            <div className="px-6 py-4 border-b border-slate-100 bg-slate-50/50 flex items-center justify-between" data-id="element-84" data-mp="2hxcsr6qlledz">
              <div className="flex items-center gap-4" data-id="element-85" data-mp="1pb1jz8su8763">
                <h3 className="font-bold text-slate-900 flex items-center gap-2" data-id="element-86" data-mp="1pbkxmdbgg9eb">
                  <Package className="w-4 h-4 text-blue-600" data-id="element-87" data-mp="1larc1qlhj6ol" />
                  Order Items ({items.length})
                </h3>
                {order.status === 'Picking' && <button onClick={() => {
                setShowGlobalScanModal(true);
                setIsBulkScanning(true);
              }} className={cn('flex items-center gap-2 px-3 py-1 rounded-full text-xs font-bold transition-all', isBulkScanning ? 'bg-blue-600 text-white shadow-lg shadow-blue-200' : 'bg-slate-100 text-slate-600 hover:bg-slate-200')} data-id="element-88" data-mp="1u9w90sheeryn">
                    <Scan className="w-3.5 h-3.5" data-id="element-89" data-mp="346b5n1uo5t6h" />
                    {isBulkScanning ? 'Bulk Scan Active' : 'Enable Bulk Scan'}
                  </button>}
              </div>
              {order.status === 'Picking' && <div className="text-xs font-bold text-blue-600 bg-blue-50 px-2 py-1 rounded" data-id="element-90" data-mp="4tah6shcgxgb">
                  {items.filter(i => i.status === 'Picked').length} /{' '}
                  {items.length} Fully Picked
                </div>}
            </div>

            <div className="divide-y divide-slate-100" data-id="element-91" data-mp="2bjgooex29bxr">
              {items.map(item => <div key={item.id} className={cn('p-6 flex items-center justify-between group transition-colors', item.status === 'Picked' && 'bg-emerald-50/30', item.status === 'Unavailable' && 'bg-slate-50 opacity-70')} data-id="element-92" data-mp="2gijvo4wlps8d">
                  <div className="flex gap-4" data-id="element-93" data-mp="2y8gtofz6oxck">
                    <div className="relative" data-id="element-94" data-mp="2t93vx06ry5v7">
                      <img src={item.image} alt={item.name} className={cn('w-16 h-16 rounded-xl object-cover border', item.status === 'Picked' ? 'border-emerald-200' : 'border-slate-200')} data-id="element-95" data-mp="2taf0h281nhnd" />
                      {item.status === 'Picked' && <div className="absolute -top-2 -right-2 bg-emerald-500 text-white rounded-full p-0.5 border-2 border-white" data-id="element-96" data-mp="dhzigpx279sj">
                          <CheckCircle2 className="w-3.5 h-3.5" data-id="element-97" data-mp="8pojpqe21u1k" />
                        </div>}
                    </div>
                    <div data-id="element-98" data-mp="l0z0os8savab">
                      <p className="font-bold text-slate-900" data-id="element-99" data-mp="l2a42qybi10w">{item.name}</p>
                      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-1" data-id="element-100" data-mp="pzsiuxz0nagt">
                        <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider" data-id="element-101" data-mp="uz54xgi2cdic">
                          SKU:{' '}
                          <span className="text-slate-600 font-mono" data-id="element-102" data-mp="zxq6bj91x38d">
                            {item.sku}
                          </span>
                        </p>
                        <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider" data-id="element-103" data-mp="18qbanubxr7do">
                          Barcode:{' '}
                          <span className="text-slate-600 font-mono" data-id="element-104" data-mp="1dpecl5wmcxhf">
                            {item.barcode}
                          </span>
                        </p>
                        <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider" data-id="element-105" data-mp="1np5c5m01e2od">
                          Qty:{' '}
                          <span className="text-blue-600" data-id="element-106" data-mp="2pht74ivbv4ew">
                            {item.pickedQuantity} / {item.quantity}
                          </span>
                        </p>
                        {item.isSerialized && <div className="flex items-center gap-1 px-1.5 py-0.5 bg-blue-50 text-blue-700 rounded text-[10px] font-bold uppercase border border-blue-100" data-id="element-107" data-mp="2kg3uiysgiiop">
                            <Barcode className="w-3 h-3" data-id="element-108" data-mp="388kdrcnxkgpl" />
                            Serialized
                          </div>}
                        {item.requiresPrescription && <div className="flex items-center gap-1 px-1.5 py-0.5 bg-purple-50 text-purple-700 rounded text-[10px] font-bold uppercase border border-purple-100" data-id="element-109" data-mp="33fhr7ohkm7iw">
                            <Info className="w-3 h-3" data-id="element-110" data-mp="3r2zcr2a8s9df" />
                            Rx Required
                          </div>}
                        {item.selectedLotNumber && <div className="flex items-center gap-1 px-1.5 py-0.5 bg-amber-50 text-amber-700 rounded text-[10px] font-bold uppercase border border-amber-100" data-id="element-111" data-mp="1kegw9vgle8cp">
                            <Info className="w-3 h-3" data-id="element-112" data-mp="1u63i0lqk3ptp" />
                            Lot: {item.selectedLotNumber}
                          </div>}
                        {item.status === 'Unavailable' && <div className="rounded border border-rose-100 bg-rose-50 px-1.5 py-0.5 text-[10px] font-bold uppercase text-rose-700" data-id="element-113" data-mp="11jsdx13qr6s5">Unavailable</div>}
                        {item.dispensedTradeDrugCode && item.dispensedTradeDrugCode !== item.tradeDrugCode && <div className="rounded border border-purple-100 bg-purple-50 px-1.5 py-0.5 text-[10px] font-bold uppercase text-purple-700" data-id="element-114" data-mp="2mvacn9mw3v9o">Sub: {item.dispensedTradeDrugCode}</div>}
                      </div>
                      {item.serialNumbers.length > 0 && <div className="flex flex-wrap gap-1 mt-2" data-id="element-115" data-mp="2tyi7rsz27bc0">
                          {item.serialNumbers.map(sn => <p key={sn} className="text-[10px] font-bold text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded border border-emerald-100" data-id="element-116" data-mp="2yxbp5whgvmhg">
                              SN: {sn}
                            </p>)}
                        </div>}
                    </div>
                  </div>

                  {order.status === 'Picking' && <div className="flex items-center gap-2" data-id="element-117" data-mp="2ky27sthiymbw">
                      {item.status === 'Unavailable' ? <button onClick={() => handleRestoreUnavailable(item.id)} className="rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-bold text-slate-600 transition-colors hover:border-blue-500 hover:text-blue-600" data-id="element-118" data-mp="1nhn2l684kph6">
                          Restore line
                        </button> : <>
                          <button onClick={() => handlePickItem(item.id)} className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-bold transition-colors hover:border-blue-500 hover:text-blue-600" data-id="element-119" data-mp="3rs00clsempqw">
                            {item.isSerialized ? <Scan className="h-4 w-4" data-id="element-120" data-mp="3e1egatr7s9rf" /> : <CheckCircle2 className="h-4 w-4" data-id="element-121" data-mp="3dyijx9v0lt53" />}
                            {item.status === 'Picked' ? 'Edit' : item.isSerialized ? 'Scan Serial' : 'Confirm Qty'}
                          </button>
                          <button onClick={() => handleMarkUnavailable(item.id)} className="rounded-lg px-3 py-2 text-xs font-bold text-rose-700 transition-colors hover:bg-rose-50" data-id="element-122" data-mp="2z3cb6f4jbg82">
                            Mark unavailable
                          </button>
                        </>}
                      {fulfillmentProfile.requiresPrescriptionReview && <button onClick={() => {
                  setScanningItem(item);
                  setShowLabelModal(true);
                }} className="rounded-lg px-3 py-2 text-xs font-bold text-purple-700 transition-colors hover:bg-purple-50" data-id="element-123" data-mp="y7o8482asevv">
                          Print label
                        </button>}
                    </div>}
                </div>)}
            </div>
          </div>

          {/* Branch Routing Flow */}
          {order.routingHistory && order.routingHistory.length > 0 && <div className="bg-blue-50 border border-blue-100 rounded-2xl p-6 shadow-sm" data-id="element-124" data-mp="29nuzwkga50g7">
              <h3 className="text-xs font-black text-blue-400 uppercase tracking-widest mb-4 flex items-center gap-2" data-id="element-125" data-mp="3u786gazw2at2">
                <Globe className="w-4 h-4" data-id="element-126" data-mp="3p5ixd0w8877m" />
                Branch Routing Flow
              </h3>
              <div className="flex items-center gap-4 overflow-x-auto pb-2" data-id="element-127" data-mp="7t70e9aup0av">
                <div className="flex items-center shrink-0" data-id="element-128" data-mp="2yt1vy0w6d1z">
                  <div className="flex flex-col items-center gap-2" data-id="element-129" data-mp="gr94ixpvs92d">
                    <div className="w-10 h-10 rounded-full bg-white border-2 border-blue-200 flex items-center justify-center text-blue-600 shadow-sm" data-id="element-130" data-mp="gtmy124wxd7t">
                      <MapPin className="w-5 h-5" data-id="element-131" data-mp="qoncsa1nhrjl" />
                    </div>
                    <div className="text-center" data-id="element-132" data-mp="lqlsxmweho72">
                      <p className="text-xs font-bold text-slate-900" data-id="element-133" data-mp="loskc2288twf">
                        Original Branch
                      </p>
                      <p className="text-[10px] text-slate-400 font-medium" data-id="element-134" data-mp="2nirv8mbft98l">
                        Innova Main
                      </p>
                    </div>
                  </div>
                </div>

                {order.routingHistory.map((route, idx) => <Fragment key={idx} data-id="element-135" data-mp="wg1e6vdeiezr" data-mpi={idx}>
                    <div className="flex flex-col items-center" data-id="element-136" data-mp="10fijnostvc4x">
                      <ChevronRight className="w-5 h-5 text-blue-300" data-id="element-137" data-mp="15ev6i9jp8j7i" />
                    </div>
                    <div className="flex items-center shrink-0" data-id="element-138" data-mp="15ciipfr9femj">
                      <div className="flex flex-col items-center gap-2" data-id="element-139" data-mp="1adg5y6r4myuw">
                        <div className="w-10 h-10 rounded-full bg-blue-600 flex items-center justify-center text-white shadow-md shadow-blue-200" data-id="element-140" data-mp="1fc0wtfkho66o">
                          <MapPin className="w-5 h-5" data-id="element-141" data-mp="1k655c39ekm9v" />
                        </div>
                        <div className="text-center" data-id="element-142" data-mp="1o4kxsmo96muo">
                          <p className="text-xs font-bold text-slate-900" data-id="element-143" data-mp="1t58pzbo7x9qh">
                            {route.branchName}
                          </p>
                          <p className="text-[10px] text-blue-500 font-bold uppercase" data-id="element-144" data-mp="1y1fync800zc4">
                            {route.timestamp.split(',')[1]}
                          </p>
                        </div>
                      </div>
                    </div>
                  </Fragment>)}
              </div>
            </div>}

          {/* Audit Trail */}
          <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm" data-id="element-145" data-mp="1u9ak54ktn9q9">
            <h3 className="font-bold text-slate-900 mb-6 flex items-center gap-2" data-id="element-146" data-mp="2qwnkcx69bopk">
              <Clock className="w-4 h-4 text-blue-600" data-id="element-147" data-mp="2lzx1y36zch0z" />
              Full Order Audit Trail
            </h3>
            <div className="space-y-6 relative before:absolute before:left-[19px] before:top-2 before:bottom-2 before:w-0.5 before:bg-slate-100" data-id="element-148" data-mp="2h6ugyjg9qfwe">
              {order.history?.map((event, idx) => <div key={event.id} className="relative pl-12" data-id="element-149" data-mp="39p9khto761fy">
                  <div className="absolute left-0 top-0 w-10 h-10 rounded-full bg-white border-2 border-slate-100 flex items-center justify-center z-10" data-id="element-150" data-mp="34x81lowixcgy">
                    <div className="w-2 h-2 rounded-full bg-blue-500" data-id="element-151" data-mp="3okgybxvc1855" />
                  </div>
                  <div className="p-4 bg-slate-50 rounded-xl border border-slate-100" data-id="element-152" data-mp="3tdtedjly46ma">
                    <div className="flex justify-between items-start" data-id="element-153" data-mp="3ep5krivnu6yz">
                      <div data-id="element-154" data-mp="3jlw2ean45ami">
                        <p className="text-sm font-bold text-slate-900" data-id="element-155" data-mp="3jn713w1pbs6r">
                          {event.status}
                        </p>
                        <p className="text-xs text-slate-500" data-id="element-156" data-mp="g5yqnx9azum2">
                          {event.message}
                        </p>
                        <p className="mt-1 text-[10px] font-bold uppercase tracking-wider text-slate-400" data-id="element-157" data-mp="17o5xiz49hnt">
                          {event.actorName} · {event.actor}
                        </p>
                      </div>
                      <p className="text-[10px] font-bold text-slate-400" data-id="element-158" data-mp="2lxdkrvztl3gi">
                        {event.timestamp}
                      </p>
                    </div>
                  </div>
                </div>)}
            </div>
          </div>
        </div>

        <div className="space-y-6" data-id="element-159" data-mp="1408rgxv63bub">
          {/* Order Source Details */}
          <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm" data-id="element-160" data-mp="1i0tds40ajpsk">
            <h3 className="font-bold text-slate-900 mb-4 flex items-center gap-2" data-id="element-161" data-mp="1dzhs29kjfopn">
              <Globe className="w-4 h-4 text-blue-600" data-id="element-162" data-mp="1dxn8ayl062n5" />
              Order Source
            </h3>
            <div className="grid grid-cols-2 gap-4" data-id="element-163" data-mp="q7j3wozzpyv3">
              <div data-id="element-164" data-mp="lbvi8jkpvnuc">
                <p className="text-[10px] font-bold text-slate-400 uppercase mb-1" data-id="element-165" data-mp="lcnbvour7bhz">
                  Source
                </p>
                <div className="flex items-center gap-2" data-id="element-166" data-mp="ubii30xsslla">
                  {order.channel === 'Website' ? <div className="flex items-center gap-1.5 px-2 py-1 bg-indigo-50 text-indigo-700 rounded-md text-[10px] font-bold uppercase" data-id="element-167" data-mp="6lo60llaz123">
                      <Globe className="w-3 h-3" data-id="element-168" data-mp="2jt71nqugzrh" />
                      Website
                    </div> : <div className="flex items-center gap-1.5 px-2 py-1 bg-orange-50 text-orange-700 rounded-md text-[10px] font-bold uppercase" data-id="element-169" data-mp="bv9g6krfxx94">
                      <Smartphone className="w-3 h-3" data-id="element-170" data-mp="lqkx99qbngk1" />
                      Aggregator
                    </div>}
                </div>
              </div>
              <div data-id="element-171" data-mp="3p832raimgbha">
                <p className="text-[10px] font-bold text-slate-400 uppercase mb-1" data-id="element-172" data-mp="3p7jnk1kcw16y">
                  Source ID
                </p>
                <p className="text-sm font-bold text-slate-900" data-id="element-173" data-mp="3kat3l35fklg9">
                  {order.sourceOrderId || 'N/A'}
                </p>
              </div>
            </div>
          </div>

          {order.channel !== 'Aggregator' && <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm" data-id="element-174" data-mp="36hv3x4lyg76w">
            <div className="flex items-center justify-between mb-4" data-id="element-175" data-mp="36fifq9plt0le">
              <h3 className="font-bold text-slate-900 flex items-center gap-2" data-id="element-176" data-mp="31i8rgxdyauz5">
                <User className="w-4 h-4 text-blue-600" data-id="element-177" data-mp="1gzogrcrrippn" />
                Customer Profile
              </h3>
              {order.isLoyaltyCustomer && <span className="px-2 py-0.5 bg-amber-100 text-amber-700 rounded text-[9px] font-black uppercase tracking-widest border border-amber-200" data-id="element-178" data-mp="172juze1tww9d">
                  Loyalty Member
                </span>}
            </div>
            <div className="space-y-4" data-id="element-179" data-mp="1zs8xjvu6t4d3">
              <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-xl border border-slate-100" data-id="element-180" data-mp="1zp4nwixydgf1">
                <div className="w-10 h-10 rounded-full bg-white border border-slate-200 flex items-center justify-center shrink-0 shadow-sm" data-id="element-181" data-mp="24nxwbwyduh8c">
                  <User className="w-5 h-5 text-slate-400" data-id="element-182" data-mp="1ur32vsgyv70x" />
                </div>
                <div data-id="element-183" data-mp="fauh7t3hqwbv">
                  <p className="text-sm font-bold text-slate-900" data-id="element-184" data-mp="fab2smd1uq2l">
                    {order.customer.name}
                  </p>
                  <p className="text-[10px] text-slate-500 font-medium uppercase tracking-wider" data-id="element-185" data-mp="jauv9cmoh5ie">
                    {order.customer.gender || 'N/A'} • {order.customer.dateOfBirth || 'DOB unavailable'}
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4" data-id="element-186" data-mp="2c8rut0afp6nv">
                <div className="space-y-1" data-id="element-187" data-mp="27aq885dsup7n">
                  <p className="text-[10px] font-bold text-slate-400 uppercase" data-id="element-188" data-mp="279yopsxh8hxq">
                    City
                  </p>
                  <p className="text-xs font-bold text-slate-700" data-id="element-189" data-mp="237211nt2xvmx">
                    {order.address.city || 'N/A'}
                  </p>
                </div>
                <div className="space-y-1" data-id="element-190" data-mp="1tc0bwryt0tkc">
                  <p className="text-[10px] font-bold text-slate-400 uppercase" data-id="element-191" data-mp="1td1q35b49w6a">
                    District
                  </p>
                  <p className="text-xs font-bold text-slate-700" data-id="element-192" data-mp="3npi3cq2byakn">
                    {order.address.district || 'N/A'}
                  </p>
                </div>
              </div>

              <div className="space-y-1 pt-2 border-t border-slate-50" data-id="element-193" data-mp="3r6vzqhjjm7g3">
                <p className="text-[10px] font-bold text-slate-400 uppercase flex items-center gap-1" data-id="element-194" data-mp="3r6npyyipvi94">
                  <MapPin className="w-3 h-3" data-id="element-195" data-mp="3v6ejvb3jiehv" /> Address
                </p>
                <p className="text-xs text-slate-600 leading-relaxed" data-id="element-196" data-mp="3ud88l9wu8j8">
                  {order.address.line1}, {order.address.zipCode || 'ZIP unavailable'}
                </p>
                {order.address.nationalShortAddress && <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400" data-id="element-197" data-mp="38aedgfngqzr8">
                    National address: {order.address.nationalShortAddress}
                  </p>}
              </div>

              <div className="space-y-1" data-id="element-198" data-mp="3m8cgiowc39rf">
                <p className="text-[10px] font-bold text-slate-400 uppercase flex items-center gap-1" data-id="element-199" data-mp="3m949dryjqte0">
                  <Phone className="w-3 h-3" data-id="element-200" data-mp="wi945nrnnwey" /> Phone
                </p>
                <p className="text-xs font-bold text-blue-600" data-id="element-201" data-mp="11gaki0xp5hmq">
                  {order.customer.mobile}
                </p>

              </div>
            </div>
          </div>}

          {order.channel !== 'Aggregator' && order.customer.nationalId && <div className="rounded-2xl border border-sky-100 bg-sky-50/50 p-6 shadow-sm" data-id="element-202" data-mp="2jfqcszwi7njk">
              <h3 className="mb-4 flex items-center gap-2 font-bold text-slate-900" data-id="element-203" data-mp="2egdpyf5muggz"><ShieldCheck className="h-4 w-4 text-sky-600" data-id="element-204" data-mp="2ei8fk6rumwre" />National ID</h3>
              <div className="flex items-center justify-between rounded-xl border border-sky-100 bg-white p-3" data-id="element-205" data-mp="2tbtp2yl2l2k8">
                <div data-id="element-206" data-mp="2oe1ozha9ua4y"><p className="text-[10px] font-bold uppercase tracking-wider text-slate-400" data-id="element-207" data-mp="2oc7dsuosxwe2">Verified patient identity</p><p className="mt-1 font-mono text-sm font-black text-slate-900" data-id="element-208" data-mp="327t18xfe084d">{order.customer.nationalId}</p></div>
                <span className="rounded-full bg-emerald-50 px-2 py-1 text-[10px] font-black uppercase text-emerald-700" data-id="element-209" data-mp="3c3w11wmrnuo5">On file</span>
              </div>
            </div>}

          {order.insurance && <div className="rounded-2xl border border-violet-100 bg-white p-6 shadow-sm" data-id="element-210" data-mp="3qq1r765fors4">
              <div className="mb-4 flex items-center justify-between" data-id="element-211" data-mp="3vpeajgx3jgpy"><h3 className="flex items-center gap-2 font-bold text-slate-900" data-id="element-212" data-mp="3vowhsu9n7kms"><ShieldCheck className="h-4 w-4 text-violet-600" data-id="element-213" data-mp="3gt6r4itiijbq" />Insurance</h3><span className="rounded-full bg-violet-50 px-2 py-1 text-[10px] font-black uppercase text-violet-700" data-id="element-214" data-mp="3lri2wz3db3wl">{order.insurance.paymentLinkStatus}</span></div>
              <div className="space-y-2 rounded-xl border border-violet-100 bg-violet-50/60 p-3" data-id="element-215" data-mp="37yikopc7avlc"><p className="text-xs font-bold text-slate-900" data-id="element-216" data-mp="3crlkhf7kn19z">{order.insurance.payerName} · {order.insurance.policyNo}</p><p className="text-[10px] text-slate-500" data-id="element-217" data-mp="2y38o4h4kngey">Member {order.insurance.memberId} · Copay {order.insurance.copayPercent}% · Approval {order.insurance.approvalNo || 'pending'}</p><p className="text-[10px] font-bold text-violet-700" data-id="element-218" data-mp="2p0zb9kltzaye">Patient {order.insurance.patientShare.toFixed(2)} SAR · Insurer {order.insurance.insuranceShare.toFixed(2)} SAR</p>
                {order.status === 'Awaiting Payment' && fulfillmentProfile.requiresPaymentLink && <div className="mt-3 space-y-3 border-t border-violet-100 pt-3" data-id="element-219" data-mp="zlwxr5nvhhf7"><p className="text-[10px] leading-4 text-violet-800" data-id="element-220" data-mp="urstiu5zrnht">Payment link sent to {order.customer.mobile}</p><div className="grid grid-cols-2 gap-2" data-id="element-221" data-mp="puty88aldai1"><button onClick={() => onUpdateStatus('Picking', {
                  insurance: {
                    ...order.insurance,
                    paymentLinkStatus: 'Paid'
                  }
                })} className="rounded-lg bg-emerald-600 px-2 py-2 text-[10px] font-black uppercase text-white transition-colors hover:bg-emerald-700" data-id="element-222" data-mp="kv7o47q28adg">Payment received</button><button onClick={() => onUpdateStatus('Held', {
                  holdReason: 'Payment link expired',
                  insurance: {
                    ...order.insurance,
                    paymentLinkStatus: 'Expired'
                  }
                })} className="rounded-lg border border-rose-200 bg-white px-2 py-2 text-[10px] font-black uppercase text-rose-700 transition-colors hover:bg-rose-50" data-id="element-223" data-mp="1dko94kbcoozn">Link expired</button></div></div>}
              </div>
            </div>}

              {order.prescription && <div className="rounded-2xl border border-purple-100 bg-white p-6 shadow-sm" data-id="element-224" data-mp="24rn6e9ooa3sn">
              <div className="mb-4 flex items-center justify-between" data-id="element-225" data-mp="28sypdwd1jqrv"><h3 className="flex items-center gap-2 font-bold text-slate-900" data-id="element-226" data-mp="28ti3t33hfx15"><Info className="h-4 w-4 text-purple-600" data-id="element-227" data-mp="2drjr607xyiif" />Prescription</h3><span className="text-[10px] font-bold text-purple-600" data-id="element-228" data-mp="2dq8k9rj78un3">{order.prescription.erxReference}</span></div>
              <div className="space-y-3 rounded-xl border border-purple-100 bg-purple-50/60 p-3" data-id="element-229" data-mp="2nhv3ob5oy00x">{order.prescription.activities.map(activity => <div key={activity.genericCode} className="space-y-0.5" data-id="element-230" data-mp="2sg6b6f8ijyfz"><p className="text-[10px] leading-4 text-slate-600" data-id="element-231" data-mp="2sismmpyiyy3h">{activity.genericCode}: {activity.instructionsAr} · {activity.durationDays} days</p><p className="text-[9px] font-medium text-purple-600" data-id="element-232" data-mp="31hw4xrpvb9gx">Allowed: {order.prescription?.acceptableTradeDrugs[activity.genericCode]?.join(', ') || 'No mapping supplied'}</p></div>)}</div>
            </div>}

          {/* Shipping & Courier Details */}
          <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm" data-id="element-233" data-mp="3915f3n677c71">
            <h3 className="font-bold text-slate-900 mb-4 flex items-center gap-2" data-id="element-234" data-mp="38ysn0h6q7lg9">
              <Truck className="w-4 h-4 text-blue-600" data-id="element-235" data-mp="34zbibpz4isc5" />
              Shipping Details
            </h3>
            <div className="space-y-4" data-id="element-236" data-mp="3it2qov14c66a">
              <div className="space-y-2 rounded-xl border border-slate-100 bg-slate-50 p-3" data-id="element-237" data-mp="3drdevd62noh5">
                <div className="flex items-center justify-between" data-id="element-238" data-mp="2q21acqlow4l6">
                  <span className="text-xs font-bold uppercase text-slate-500" data-id="element-239" data-mp="2q4dpkw02c0uj">Fulfillment profile</span>
                  <span className="text-sm font-black uppercase tracking-tight text-blue-600" data-id="element-240" data-mp="2m327dbjiqhwd">{fulfillmentProfile.kind}</span>
                </div>
                <p className="text-[10px] font-medium leading-4 text-slate-500" data-id="element-241" data-mp="2v0cgaq8v5uy4">
                  Dispatch and escalation are determined by the order source and payer profile.
                </p>
              </div>


              {!fulfillmentProfile.requiresPickupOtp && <div className="space-y-4" data-id="element-242" data-mp="1s8a0cyld7rc7">
                  <div className="flex justify-between items-center" data-id="element-243" data-mp="1s78edz2t9kfp">
                    <span className="text-xs font-bold text-slate-400 uppercase" data-id="element-244" data-mp="1x84o4t9ug0t1">
                      Courier
                    </span>
                    <div className="flex flex-col items-end gap-1" data-id="element-245" data-mp="1j9y34dpztvas">
                      <div className="flex items-center gap-2" data-id="element-246" data-mp="1o87z2qjjkzxg">
                        <div className="w-6 h-6 bg-slate-100 rounded flex items-center justify-center" data-id="element-247" data-mp="2bx0xctnmzjv8">
                          <Globe className="w-3 h-3 text-slate-500" data-id="element-248" data-mp="2fyvvjpa9th4s" />
                        </div>
                        <span className="text-sm font-bold text-slate-900" data-id="element-249" data-mp="227hcxjk9zhy9">
                          {order.courierName || 'Not Assigned'}
                        </span>
                      </div>
                      {order.courierPhone && <div className="flex items-center gap-1.5 text-[10px] font-bold text-blue-600 bg-blue-50 px-2 py-0.5 rounded-md" data-id="element-250" data-mp="2uu20e8xmr9wj">
                          <Phone className="w-3 h-3" data-id="element-251" data-mp="5gzt8x5ltl9j" />
                          {order.courierPhone}
                        </div>}
                    </div>
                  </div>

                  {order.driverInfo && <div className="pt-4 border-t border-slate-100" data-id="element-252" data-mp="f82pnlnl9c2h">
                      <p className="text-[10px] font-bold text-slate-400 uppercase mb-3" data-id="element-253" data-mp="j8wd6dx4couu">
                        Assigned Driver
                      </p>
                      <div className="flex items-center gap-3" data-id="element-254" data-mp="3ipkz1xj0er50">
                        <img src={order.driverInfo.avatar} alt={order.driverInfo.name} className="w-10 h-10 rounded-full border-2 border-white shadow-sm" data-id="element-255" data-mp="3nne24hp0n512" />
                        <div className="flex-1" data-id="element-256" data-mp="16xz3091wpo0f">
                          <p className="text-sm font-bold text-slate-900" data-id="element-257" data-mp="16xfmmwrwn7oi">
                            {order.driverInfo.name}
                          </p>
                          <p className="text-[10px] text-slate-500 font-mono" data-id="element-258" data-mp="1m0pd9atae1os">
                            {order.driverInfo.vehiclePlate}
                          </p>
                        </div>
                        <button className="p-2 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition-colors" data-id="element-259" data-mp="1caxk8l0u5gmf">
                          <Phone className="w-4 h-4" data-id="element-260" data-mp="188j07xlfkatk" />
                        </button>
                      </div>
                    </div>}
                </div>}
            </div>
          </div>

          {/* Payment & Loyalty */}
          <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm" data-id="element-261" data-mp="tcbir51epnhz">
            <h3 className="font-bold text-slate-900 mb-4 flex items-center gap-2" data-id="element-262" data-mp="te4pekc0s7pz">
              <CreditCard className="w-4 h-4 text-blue-600" data-id="element-263" data-mp="pcj88msgd79z" />
              Payment & Loyalty
            </h3>
            <div className="space-y-4" data-id="element-264" data-mp="2no89swvxsrci">
              {/* Main Totals */}
              <div className="space-y-2" data-id="element-265" data-mp="2jp8y8tfp9wc3">
                <div className="flex justify-between text-sm" data-id="element-266" data-mp="h228jvgnuml5">
                  <span className="text-slate-500" data-id="element-267" data-mp="h0r9g8y5u30d">Subtotal</span>
                  <span className="font-medium text-slate-900" data-id="element-268" data-mp="ltu3sjarhghm">
                    {((order.totalAmount + (order.discountAmount || 0)) / 1.15).toFixed(2)}{' '}
                    SAR
                  </span>
                </div>
                <div className="flex justify-between text-sm" data-id="element-269" data-mp="zsbk3vyc7qpi">
                  <span className="text-slate-500" data-id="element-270" data-mp="14ro7qix191t5">VAT (15%)</span>
                  <span className="font-medium text-slate-900" data-id="element-271" data-mp="14qvac8jyrono">
                    {((order.totalAmount + (order.discountAmount || 0)) * 0.15 / 1.15).toFixed(2)}{' '}
                    SAR
                  </span>
                </div>
              </div>

              {/* Discounts & Loyalty Breakdown */}
              {(order.discountAmount || order.loyaltyPointsUsed || order.qitafAmount || order.walletAmount) && <div className="p-3 bg-emerald-50/50 rounded-xl border border-emerald-100 space-y-2" data-id="element-272" data-mp="33zd5by0ynzh3">
                  {order.discountAmount && <div className="flex justify-between text-xs" data-id="element-273" data-mp="3hw9zo9gafz2q">
                      <div className="flex items-center gap-2" data-id="element-274" data-mp="3d0kwzx8z2227">
                        <span className="text-emerald-700 font-bold uppercase" data-id="element-275" data-mp="2p95v25l3xmrh">
                          Coupon
                        </span>
                        <span className="px-1.5 py-0.5 bg-white text-emerald-700 rounded text-[9px] font-black border border-emerald-200 uppercase" data-id="element-276" data-mp="2kc6y7fa271pl">
                          {order.couponCode}
                        </span>
                      </div>
                      <span className="font-bold text-emerald-700" data-id="element-277" data-mp="26io2q246st6n">
                        -{order.discountAmount.toFixed(2)} SAR
                      </span>
                    </div>}
                  {order.loyaltyPointsUsed && <div className="flex justify-between text-xs" data-id="element-278" data-mp="14ukg49380p26">
                      <span className="text-emerald-700 font-bold uppercase" data-id="element-279" data-mp="19svqckxfh1kx">
                        Loyalty Points ({order.loyaltyPointsUsed})
                      </span>
                      <span className="font-bold text-emerald-700" data-id="element-280" data-mp="1iknwwkjbzqk8">
                        Redeemed
                      </span>
                    </div>}
                  {order.qitafAmount && <div className="flex justify-between text-xs" data-id="element-281" data-mp="1sh93397377pn">
                      <span className="text-emerald-700 font-bold uppercase" data-id="element-282" data-mp="1xhewouwx34ad">
                        Qitaf Points
                      </span>
                      <span className="font-bold text-emerald-700" data-id="element-283" data-mp="21hp5sgg0aatg">
                        -{order.qitafAmount.toFixed(2)} SAR
                      </span>
                    </div>}
                  {order.walletAmount && <div className="flex justify-between text-xs" data-id="element-284" data-mp="3vqu997v6sqdy">
                      <span className="text-emerald-700 font-bold uppercase" data-id="element-285" data-mp="3vs5d17omty52">
                        Wallet Balance
                      </span>
                      <span className="font-bold text-emerald-700" data-id="element-286" data-mp="3m0rhfazsxih7">
                        -{order.walletAmount.toFixed(2)} SAR
                      </span>
                    </div>}
                </div>}

              <div className="pt-3 border-t border-slate-100 flex justify-between items-center" data-id="element-287" data-mp="ij11doyupubx">
                <span className="font-bold text-slate-900" data-id="element-288" data-mp="dmiwmp3itvzl">Grand Total</span>
                <span className="text-lg font-black text-blue-600" data-id="element-289" data-mp="9iuni1z8qsc8">
                  {order.totalAmount.toFixed(2)} SAR
                </span>
              </div>

              {/* Payment Method Selector */}
              <div className="mt-4 space-y-3" data-id="element-290" data-mp="2uawg0fyo62c6">
                <div className={cn('p-3 rounded-xl flex items-center justify-between border', order.paymentType === 'COD' ? 'bg-amber-50 border-amber-100' : 'bg-blue-50 border-blue-100')} data-id="element-291" data-mp="2u7ifzedha57w">
                  <div className="flex items-center gap-2" data-id="element-292" data-mp="2hrsj8t5ljw6t">
                    <Info className={cn('w-4 h-4', order.paymentType === 'COD' ? 'text-amber-600' : 'text-blue-600')} data-id="element-293" data-mp="2htbxyfbltz6h" />
                    <div className="flex flex-col" data-id="element-294" data-mp="2cth9eifaxlol">
                      <span className="text-[9px] font-black text-slate-400 uppercase tracking-tighter" data-id="element-295" data-mp="1y3s1838skhet">
                        Payment Method
                      </span>
                      <span className={cn('text-xs font-black uppercase', order.paymentType === 'COD' ? 'text-amber-700' : 'text-blue-700')} data-id="element-296" data-mp="1p2ljjquep0jz">
                        {order.paymentType}
                      </span>
                    </div>
                  </div>
                  {!readOnly && !isPaymentMethodLocked && !['Out for Delivery', 'Delivered', 'Delivery Attempt Failed'].includes(order.status) && <button onClick={() => setIsEditingPayment(!isEditingPayment)} className="px-3 py-1 bg-white border border-slate-200 rounded-lg text-[10px] font-black text-blue-600 hover:border-blue-500 uppercase shadow-sm transition-all" data-id="element-297" data-mp="1ogfs2w4mdtfr">
                        {isEditingPayment ? 'Cancel' : 'Change'}
                      </button>}
                </div>

                {isPaymentMethodLocked && <p className="text-[10px] font-bold text-slate-400" data-id="element-298" data-mp="3eje6s1w11xy4">Payment method is locked for {order.payerType} orders.</p>}

                {isEditingPayment && !isPaymentMethodLocked && <div className="grid grid-cols-2 gap-2 p-2 bg-slate-50 rounded-xl border border-slate-100 animate-in fade-in slide-in-from-top-1" data-id="element-299" data-mp="3nkcfgxheutn1">
                    {['COD', 'Visa', 'Mada', 'ApplePay', 'Tamara', 'Tabby'].map(method => <button key={method} onClick={() => {
                  onUpdatePaymentMethod?.(method as PaymentType);
                  setIsEditingPayment(false);
                }} className={cn('px-3 py-2 rounded-lg text-[10px] font-bold uppercase transition-all', order.paymentType === method ? 'bg-blue-600 text-white shadow-sm' : 'bg-white border border-slate-200 text-slate-600 hover:border-blue-300')} data-id="element-300" data-mp="2ztfa63k4eukr">
                          {method}
                        </button>)}
                  </div>}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modals */}
      <AnimatePresence data-id="element-301" data-mp="1qmeq6t13uuv7">
        {showGlobalScanModal && <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm" data-id="element-302" data-mp="1ltlm87dgnqk6">
            <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} exit={{
          opacity: 0,
          scale: 0.95
        }} className="bg-white rounded-3xl w-full max-w-md overflow-hidden shadow-2xl" data-id="element-303" data-mp="1gw27z7grjds8">
              <div className="p-6 border-b border-slate-100 flex items-center justify-between" data-id="element-304" data-mp="3nwnon7vunw11">
                <h3 className="font-bold text-slate-900" data-id="element-305" data-mp="3jw3odvfzccao">
                  {isBulkScanning ? 'Continuous Picking' : scanningItem ? 'Confirm Quantity' : 'Scan Item'}
                </h3>
                <button onClick={() => {
              setShowGlobalScanModal(false);
              setIsBulkScanning(false);
              setScanningItem(null);
            }} className="p-1 hover:bg-slate-100 rounded-full" data-id="element-306" data-mp="34yl16xiya75c">
                  <XCircle className="w-5 h-5 text-slate-400" data-id="element-307" data-mp="2r7pthok6tw3c" />
                </button>
              </div>
              <div className="p-8 space-y-6" data-id="element-308" data-mp="2m6saj9rcsy0s">
                {scanningItem ? <div className="space-y-6" data-id="element-309" data-mp="2h8gzwwt8ijhi">
                    <div className="flex flex-col items-center text-center space-y-3" data-id="element-310" data-mp="2h9il3u3ysmcy">
                      <div className="w-16 h-16 bg-blue-50 rounded-2xl flex items-center justify-center text-blue-600" data-id="element-311" data-mp="274aga8re85ru">
                        <Package className="w-8 h-8" data-id="element-312" data-mp="2c5pyzu63m072" />
                      </div>
                      <div data-id="element-313" data-mp="2h3rg49jyrpfw">
                        <h4 className="font-bold text-xl text-slate-900" data-id="element-314" data-mp="2m22pyka9dzy4">
                          {scanningItem.name}
                        </h4>
                        <p className="text-xs font-bold text-slate-400 uppercase tracking-wider" data-id="element-315" data-mp="1ofn8hihu12ak">
                          SKU: {scanningItem.sku}
                        </p>
                      </div>
                      <div className="flex items-center gap-8 pt-2" data-id="element-316" data-mp="1ybqc6ssc18zm">
                        <div className="text-center" data-id="element-317" data-mp="233qdopo1w9yb">
                          <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest" data-id="element-318" data-mp="235bdatp82j31">
                            Ordered
                          </p>
                          <p className="text-lg font-black text-slate-900" data-id="element-319" data-mp="1almhjax4gjkr">
                            {scanningItem.quantity}
                          </p>
                        </div>
                        <div className="text-center" data-id="element-320" data-mp="xm3nrk0n0ktz">
                          <p className="text-[10px] font-black text-emerald-400 uppercase tracking-widest" data-id="element-321" data-mp="1cc1f28tv5qne">
                            Picked
                          </p>
                          <p className="text-lg font-black text-emerald-600" data-id="element-322" data-mp="17dq3ntnx7832">
                            {scanningItem.pickedQuantity}
                          </p>
                        </div>
                        <div className="text-center" data-id="element-323" data-mp="eq3qbtow4p30">
                          <p className="text-[10px] font-black text-blue-400 uppercase tracking-widest" data-id="element-324" data-mp="tm2y13838n7m">
                            Remaining
                          </p>
                          <p className="text-lg font-black text-blue-600" data-id="element-325" data-mp="250otbkg73frg">
                            {scanningItem.quantity - scanningItem.pickedQuantity}
                          </p>
                        </div>
                      </div>
                    </div>

                    {scanningItem.lotNumbers && <div className="space-y-3" data-id="element-326" data-mp="2cl0udntulkxh">
                        <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest" data-id="element-327" data-mp="1ykgbkrnxnp3z">
                          Select Lot Number
                        </label>
                        <div className="grid gap-2" data-id="element-328" data-mp="22nv2wehbnrwq">
                          {scanningItem.lotNumbers.map(lot => {
                    const therapyEnd = fulfillmentProfile.requiresPrescriptionReview ? getTherapyEndDate(scanningItem.genericCode) : null;
                    const expiresBeforeTherapyEnds = therapyEnd ? new Date(`${lot.expiryDate}-01`).getTime() < therapyEnd.getTime() : false;
                    return <button key={lot.number} onClick={() => setTempLot(lot.number)} className={cn('flex items-center justify-between rounded-2xl border-2 p-4 text-left transition-colors', tempLot === lot.number ? 'border-blue-600 bg-blue-50/50' : expiresBeforeTherapyEnds ? 'border-amber-200 bg-amber-50/50 hover:border-amber-400' : 'border-slate-100 hover:border-slate-200')} data-id="element-329" data-mp="3e2rspqlbyl55">
                                <div data-id="element-330" data-mp="1vcrqzxieux7f">
                                  <p className="text-sm font-black text-slate-900" data-id="element-331" data-mp="1vbq5t07okubz">{lot.number}</p>
                                  <p className="text-[10px] font-bold uppercase text-slate-400" data-id="element-332" data-mp="2rv5w8xsfhu42">EXP: {lot.expiryDate}</p>
                                  {expiresBeforeTherapyEnds && <p className="mt-1 text-[10px] font-bold text-amber-700" data-id="element-333" data-mp="9l1k3gdq0bft">Expires before end of therapy</p>}
                                </div>
                                <div className="text-right" data-id="element-334" data-mp="elpdg8pf94d7"><p className="text-[10px] font-black uppercase tracking-widest text-slate-300" data-id="element-335" data-mp="ilg7qme5q2mh">Stock</p><p className="text-sm font-black text-slate-600" data-id="element-336" data-mp="nk9p4pwkedrx">{lot.stock}</p></div>
                              </button>;
                  })}
                        </div>
                      </div>}

                    {fulfillmentProfile.requiresPrescriptionReview && scanningItem.allowSubstitution && <div className="space-y-3" data-id="element-337" data-mp="12ba4zfzn59y3">
                        <label className="text-[10px] font-black uppercase tracking-widest text-purple-600" data-id="element-338" data-mp="16b0w5kt2xs35">Approved trade drug</label>
                        <div className="grid gap-2" data-id="element-339" data-mp="1bbos8kw60z48">
                          {(order.prescription?.acceptableTradeDrugs[scanningItem.genericCode] || [scanningItem.tradeDrugCode]).map(tradeDrugCode => <button key={tradeDrugCode} onClick={() => setTempSubstitution(tradeDrugCode)} className={cn('rounded-xl border-2 px-4 py-3 text-left text-sm font-bold transition-colors', (tempSubstitution || scanningItem.tradeDrugCode) === tradeDrugCode ? 'border-purple-600 bg-purple-50 text-purple-800' : 'border-slate-100 bg-white text-slate-600 hover:border-purple-200')} data-id="element-340" data-mp="3ca0jgx9fbzxw">
                              {tradeDrugCode}{tradeDrugCode === scanningItem.tradeDrugCode ? ' · prescribed' : ' · approved substitute'}
                            </button>)}
                        </div>
                      </div>}

                    <div className="space-y-3" data-id="element-341" data-mp="2amzpepk14kqj">
                      <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest" data-id="element-342" data-mp="25o6acsp3glo9">
                        Confirm Picked Quantity
                      </label>
                      <div className="flex items-center gap-4" data-id="element-343" data-mp="q4fgnaheipjo">
                        <button onClick={() => setManualQty(Math.max(0, parseInt(manualQty || '0') - 1).toString())} className="w-14 h-14 rounded-2xl border-2 border-slate-100 flex items-center justify-center text-2xl font-black text-slate-400 hover:bg-slate-50 transition-colors" data-id="element-344" data-mp="q3x93hpagowg">
                          -
                        </button>
                        <div className="flex-1 relative" data-id="element-345" data-mp="1mv7m7e6pkz77">
                          <input type="number" value={manualQty} onChange={e => setManualQty(e.target.value)} className="w-full text-center py-4 bg-slate-50 border-2 border-slate-100 rounded-2xl outline-none font-black text-2xl text-slate-900 focus:border-blue-500 transition-all" data-id="element-346" data-mp="1mvr5ay83htmt" />
                        </div>
                        <button onClick={() => setManualQty(Math.min(scanningItem.quantity, parseInt(manualQty || '0') + 1).toString())} className="w-14 h-14 rounded-2xl border-2 border-slate-100 flex items-center justify-center text-2xl font-black text-slate-400 hover:bg-slate-50 transition-colors" data-id="element-347" data-mp="wb45itfqp6z">
                          +
                        </button>
                      </div>
                      {parseInt(manualQty) < scanningItem.quantity && <p className="text-[10px] font-bold text-amber-600 flex items-center gap-1.5" data-id="element-348" data-mp="1ju7gh9g7bq2z">
                          <AlertCircle className="w-3 h-3" data-id="element-349" data-mp="1orhbr5q9uvyq" />
                          Partial fulfillment:{' '}
                          {scanningItem.quantity - parseInt(manualQty)} units
                          pending
                        </p>}
                    </div>

                    <div className="flex gap-4 pt-4" data-id="element-350" data-mp="10xprgzsqm4af">
                      <button onClick={() => {
                  setScanningItem(null);
                  setTempLot('');
                  setManualQty('');
                }} className="flex-1 py-4 text-slate-500 font-bold hover:bg-slate-50 rounded-2xl transition-colors" data-id="element-351" data-mp="15vr7fbuv9nho">
                        Back to Scan
                      </button>
                      <button disabled={(scanningItem.requiresLot || scanningItem.lotNumbers && scanningItem.lotNumbers.length > 0) && !tempLot} onClick={() => {
                  handleConfirmItemFulfillment();
                }} className="flex-[1.5] py-4 bg-blue-400 text-white font-black rounded-2xl shadow-lg shadow-blue-100 hover:bg-blue-500 transition-all disabled:opacity-50" data-id="element-352" data-mp="2ra5d1y7ucazs">
                        Confirm & Next Scan
                      </button>
                    </div>
                  </div> : <div className="space-y-8" data-id="element-353" data-mp="2s5hzemvoqwik">
                    <div className="text-center space-y-2" data-id="element-354" data-mp="2s8cj2c6vkdax">
                      <div className="w-20 h-20 bg-blue-50 rounded-3xl flex items-center justify-center mx-auto text-blue-600" data-id="element-355" data-mp="mecwgf1ya0ve">
                        <Scan className="w-10 h-10" data-id="element-356" data-mp="qe3riuyif35q" />
                      </div>
                      <h4 className="font-bold text-xl text-slate-900" data-id="element-357" data-mp="2axqtiolxncvo">
                        Ready to Scan
                      </h4>
                      <p className="text-sm text-slate-500" data-id="element-358" data-mp="26tj2ulvwzmut">
                        Scan an item's barcode or serial number to begin
                        fulfillment.
                      </p>
                    </div>

                    <div className="space-y-2" data-id="element-359" data-mp="2ukojh39oij66">
                      <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest" data-id="element-360" data-mp="2ujdg34k5bdfl">
                        Scan Item
                      </label>
                      <div className="relative" data-id="element-361" data-mp="33luxn5ouvhw9">
                        <div className="absolute left-4 top-1/2 -translate-y-1/2" data-id="element-362" data-mp="33j8tlg16bkis">
                          <Scan className="w-5 h-5 text-blue-400" data-id="element-363" data-mp="2ynbax32fu22u" />
                        </div>
                        <input autoFocus type="text" value={globalScanValue} onChange={e => setGlobalScanValue(e.target.value)} onKeyDown={e => {
                    if (e.key === 'Enter') {
                      handleGlobalScan(globalScanValue);
                    }
                  }} placeholder="Scan barcode, SKU, or Lot..." className="w-full pl-12 pr-4 py-5 bg-white border-2 border-blue-500 rounded-2xl outline-none shadow-[0_0_0_4px_rgba(59,130,246,0.1)] font-mono font-bold text-slate-700 placeholder:text-slate-300 transition-all" data-id="element-364" data-mp="197u3uz85xl31" />
                      </div>
                    </div>

                    <div className="flex gap-4 pt-4" data-id="element-365" data-mp="5suws5872rsb">
                      <button onClick={() => {
                  setShowGlobalScanModal(false);
                  setIsBulkScanning(false);
                }} className="w-full py-4 text-slate-500 font-bold hover:bg-slate-50 rounded-2xl transition-colors" data-id="element-366" data-mp="5sbf8pmgi5et">
                        Cancel & Close
                      </button>
                    </div>
                  </div>}
              </div>
            </motion.div>
          </div>}
      </AnimatePresence>

      {/* Cancel Order Modal */}
      <AnimatePresence data-id="element-367" data-mp="1le5a9o0r5h3f">
        {showRejectModal && <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm" data-id="element-368" data-mp="2i2l3pv6zgalt">
            <motion.div initial={{
          opacity: 0,
          scale: 0.95,
          y: 16
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.95,
          y: 16
        }} className="bg-white rounded-3xl w-full max-w-md overflow-hidden shadow-2xl" data-id="element-369" data-mp="2e19jk56vohl0">
              <div className="p-6 border-b border-slate-100 flex items-center justify-between bg-rose-50/60" data-id="element-370" data-mp="2gj1nghp8qpgt">
                <div className="flex items-center gap-3" data-id="element-371" data-mp="2lkqwxswtev2s">
                  <div className="w-10 h-10 rounded-xl bg-rose-100 text-rose-600 flex items-center justify-center" data-id="element-372" data-mp="27jy0h2mydpx1">
                    <XCircle className="w-6 h-6" data-id="element-373" data-mp="2chpuc275rm3v" />
                  </div>
                  <div data-id="element-374" data-mp="308c0tp1ikybr">
                    <h3 className="font-bold text-slate-900" data-id="element-375" data-mp="3094xfx6re7g6">Cancel Order</h3>
                    <p className="text-[10px] font-bold text-rose-600 uppercase tracking-widest" data-id="element-376" data-mp="349fbzy8ma66n">
                      Reason required
                    </p>
                  </div>
                </div>
                <button onClick={() => {
              setShowRejectModal(false);
              setRejectionReason('');
              setCustomReason('');
            }} className="p-1 rounded-full hover:bg-white transition-colors" aria-label="Close cancellation dialog" data-id="element-377" data-mp="2vgtwcr0a8fm1">
                  <XCircle className="w-5 h-5 text-slate-400" data-id="element-378" data-mp="14poo86ncf3a" />
                </button>
              </div>

              <div className="p-6 space-y-4" data-id="element-379" data-mp="3t91x3cddbi2q">
                <p className="text-sm text-slate-600" data-id="element-380" data-mp="avu3n7wbnzy4">
                  Select why this order is being cancelled. This will be saved
                  in the order history.
                </p>
                <div className="space-y-2" data-id="element-381" data-mp="3eenehcxpuaol">
                  {cancelReasons.map(reason => <button key={reason.id} onClick={() => {
                setRejectionReason(reason.label);
                if (!reason.requiresFreeText) setCustomReason('');
              }} className={cn('w-full px-4 py-3 rounded-xl border-2 text-left text-sm font-bold transition-all', rejectionReason === reason.label ? 'border-rose-600 bg-rose-50 text-rose-700' : 'border-slate-100 bg-slate-50 text-slate-600 hover:border-slate-200')} data-id="element-382" data-mp="3ad25w3s7sik7">
                      {reason.label}
                    </button>)}
                </div>

                {cancelReasonRequiresText && <textarea value={customReason} onChange={event => setCustomReason(event.target.value)} placeholder="Enter the cancellation reason..." className="w-full min-h-[88px] p-4 rounded-xl border-2 border-slate-100 bg-slate-50 text-sm outline-none focus:border-rose-500 focus:bg-white transition-colors" data-id="element-383" data-mp="1km5h06ey88vf" />}
              </div>

              <div className="p-6 bg-slate-50 border-t border-slate-100 flex gap-3" data-id="element-384" data-mp="49gp57gl0d53">
                <button onClick={() => {
              setShowRejectModal(false);
              setRejectionReason('');
              setCustomReason('');
            }} className="flex-1 py-3.5 rounded-xl text-slate-600 font-bold hover:bg-white transition-colors" data-id="element-385" data-mp="97i8lthx6eh3">
                  Go Back
                </button>
                <button disabled={!rejectionReason || cancelReasonRequiresText && !customReason.trim()} onClick={() => {
              const reason = cancelReasonRequiresText ? customReason.trim() : rejectionReason;
              onUpdateStatus('Cancelled', {
                rejectionReason: reason
              });
              setShowRejectModal(false);
              setRejectionReason('');
              setCustomReason('');
            }} className="flex-1 py-3.5 rounded-xl bg-rose-600 text-white font-bold shadow-lg shadow-rose-100 hover:bg-rose-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-386" data-mp="3iwe13mk04oi7">
                  Confirm Cancel
                </button>
              </div>
            </motion.div>
          </div>}
      </AnimatePresence>

      {/* Reject Pending Order Modal */}
      <AnimatePresence data-id="element-387" data-mp="dxf28jftflgg">
        {showOrderRejectionModal && <div className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/60 p-4 backdrop-blur-sm" data-id="element-388" data-mp="dvki37cdbxdf">
            <motion.div initial={{
          opacity: 0,
          scale: 0.95,
          y: 16
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.95,
          y: 16
        }} className="w-full max-w-md overflow-hidden rounded-3xl bg-white shadow-2xl" role="dialog" aria-modal="true" aria-labelledby="reject-order-title" data-id="element-389" data-mp="45z30ignw6d">
              <div className="flex items-center justify-between border-b border-amber-100 bg-amber-50/70 p-6" data-id="element-390" data-mp="3he4bweap3bac">
                <div className="flex items-center gap-3" data-id="element-391" data-mp="2ypk0yf4st4cg">
                  <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-amber-100 text-amber-700" data-id="element-392" data-mp="2yq3lm3lu26u6">
                    <AlertCircle className="h-6 w-6" data-id="element-393" data-mp="2tt3gpw0pza4n" />
                  </div>
                  <div data-id="element-394" data-mp="37rtezez9zful">
                    <h3 id="reject-order-title" className="font-bold text-slate-900" data-id="element-395" data-mp="32rf3fmygtnrt">Reject Order</h3>
                    <p className="text-[10px] font-bold uppercase tracking-widest text-amber-700" data-id="element-396" data-mp="3hopmpihcnxy3">Return to system routing</p>
                  </div>
                </div>
                <button onClick={() => {
              setShowOrderRejectionModal(false);
              setOrderRejectionReason('');
              setOrderRejectionCustomReason('');
            }} className="rounded-full p-1 text-slate-400 transition-colors hover:bg-white" aria-label="Close order rejection dialog" data-id="element-397" data-mp="3cpcra9cexijw">
                  <XCircle className="h-5 w-5" data-id="element-398" data-mp="3vdzvyqqog5wy" />
                </button>
              </div>

              <div className="space-y-4 p-6" data-id="element-399" data-mp="sndqqjum4103">
                <p className="text-sm leading-6 text-slate-600" data-id="element-400" data-mp="nmq4scl8qacq">
                  Choose why this pending order cannot be accepted. The routing system will receive it for reassignment.
                </p>
                <div className="space-y-2" data-id="element-401" data-mp="xk4g5xsb48y6">
                  {rejectReasons.map(reason => <button key={reason.id} onClick={() => {
                setOrderRejectionReason(reason.label);
                if (!reason.requiresFreeText) setOrderRejectionCustomReason('');
              }} className={cn('w-full rounded-xl border-2 px-4 py-3 text-left text-sm font-bold transition-colors', orderRejectionReason === reason.label ? 'border-amber-500 bg-amber-50 text-amber-800' : 'border-slate-100 bg-slate-50 text-slate-600 hover:border-slate-200')} data-id="element-402" data-mp="1bjdk9z38ncls">
                      {reason.label}
                    </button>)}
                </div>
                {rejectReasonRequiresText && <textarea value={orderRejectionCustomReason} onChange={event => setOrderRejectionCustomReason(event.target.value)} placeholder="Enter the routing reason..." className="min-h-[88px] w-full rounded-xl border-2 border-slate-100 bg-slate-50 p-4 text-sm outline-none transition-colors focus:border-amber-500 focus:bg-white" data-id="element-403" data-mp="1zz09ar0wgf0a" />}
              </div>

              <div className="flex gap-3 border-t border-slate-100 bg-slate-50 p-6" data-id="element-404" data-mp="1l810bew5jog6">
                <button onClick={() => {
              setShowOrderRejectionModal(false);
              setOrderRejectionReason('');
              setOrderRejectionCustomReason('');
            }} className="flex-1 rounded-xl py-3.5 font-bold text-slate-600 transition-colors hover:bg-white" data-id="element-405" data-mp="178i6zs802hnx">
                  Go Back
                </button>
                <button disabled={!orderRejectionReason || rejectReasonRequiresText && !orderRejectionCustomReason.trim()} onClick={() => {
              const reason = rejectReasonRequiresText ? orderRejectionCustomReason.trim() : orderRejectionReason;
              onUpdateStatus('Rejected', {
                rejectionReason: reason
              });
              setShowOrderRejectionModal(false);
              setOrderRejectionReason('');
              setOrderRejectionCustomReason('');
            }} className="flex-1 rounded-xl bg-amber-600 py-3.5 font-bold text-white shadow-lg shadow-amber-100 transition-colors hover:bg-amber-700 disabled:cursor-not-allowed disabled:opacity-50" data-id="element-406" data-mp="344cs88qbims1">
                  Return to Routing
                </button>
              </div>
            </motion.div>
          </div>}
      </AnimatePresence>

      {/* Aggregator Unavailable Rejection Modal */}
      <AnimatePresence data-id="element-407" data-mp="vy0vgssseq42">
        {showAggregatorRejectionModal && <div className="fixed inset-0 z-[65] flex items-center justify-center bg-slate-900/60 p-4 backdrop-blur-sm" data-id="element-408" data-mp="10q2aunl94wyb">
            <motion.div initial={{
          opacity: 0,
          scale: 0.95,
          y: 16
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.95,
          y: 16
        }} className="w-full max-w-md overflow-hidden rounded-3xl bg-white shadow-2xl" role="dialog" aria-modal="true" aria-labelledby="aggregator-rejection-title" data-id="element-409" data-mp="15nkgo53z2w23">
              <div className="flex items-center gap-3 border-b border-rose-100 bg-rose-50/70 p-6" data-id="element-410" data-mp="1jn3nmul1s1ct"><div className="flex h-10 w-10 items-center justify-center rounded-xl bg-rose-100 text-rose-600" data-id="element-411" data-mp="3hf7vo5wohv93"><XCircle className="h-6 w-6" data-id="element-412" data-mp="3lezy00vhb560" /></div><div data-id="element-413" data-mp="3lg1i0uuh31zv"><h3 id="aggregator-rejection-title" className="font-bold text-slate-900" data-id="element-414" data-mp="3lhufbjj749v7">Cancel order — item unavailable</h3><p className="text-[10px] font-bold uppercase tracking-widest text-rose-600" data-id="element-415" data-mp="2q7o3fw82la18">{order.channel === 'Yaser eRx' ? 'No substitute available under this generic code' : 'Partner orders cannot be rejected or partially fulfilled'}</p></div></div>
              <div className="space-y-4 p-7" data-id="element-416" data-mp="2v5ppmq0slpgx"><p className="text-sm leading-6 text-slate-600" data-id="element-417" data-mp="2v5hassl40a33">This order has a short or unavailable line and {order.channel === 'Yaser eRx' ? 'no approved substitute exists' : 'partner orders cannot be partially fulfilled'}. It must be cancelled and the source notified — there is no reject option for this order type.</p><div className="rounded-xl border border-rose-100 bg-rose-50 p-4 text-sm font-bold text-rose-800" data-id="element-418" data-mp="2h6zs59a29ns1">Reason: Out of stock</div></div>
              <div className="flex gap-3 border-t border-slate-100 bg-slate-50 p-6" data-id="element-419" data-mp="1oo2i89lra7mu"><button onClick={() => setShowAggregatorRejectionModal(false)} className="flex-1 rounded-xl py-3.5 font-bold text-slate-600 transition-colors hover:bg-white" data-id="element-420" data-mp="1om9dizupdxhh">Go Back</button><button onClick={() => {
              onUpdateStatus('Cancelled', {
                rejectionReason: 'Out of stock'
              });
              setShowAggregatorRejectionModal(false);
            }} className="flex-1 rounded-xl bg-rose-600 py-3.5 font-bold text-white shadow-lg shadow-rose-100 transition-colors hover:bg-rose-700" data-id="element-421" data-mp="3vbb97fmmc8sj">Cancel order</button></div>
            </motion.div>
          </div>}
      </AnimatePresence>

      {/* Partial Fulfillment Modal */}
      <AnimatePresence data-id="element-422" data-mp="hvvkx6v8a9a2">
        {showPartialModal && <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm" data-id="element-423" data-mp="mw00muobazz8">
            <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} exit={{
          opacity: 0,
          scale: 0.95
        }} className="bg-white rounded-[32px] w-full max-w-lg overflow-hidden shadow-2xl" data-id="element-424" data-mp="8ws120o8lpui">
              <div className="p-6 border-b border-slate-100 flex items-center justify-between" data-id="element-425" data-mp="1487ze8052t8y">
                <h3 className="font-bold text-slate-900" data-id="element-426" data-mp="z9mx831btzhv">
                  Partial Fulfillment
                </h3>
                <button onClick={() => setShowPartialModal(false)} className="p-1 hover:bg-slate-100 rounded-full transition-colors" data-id="element-427" data-mp="qeyvunyvh1yc">
                  <XCircle className="w-5 h-5 text-slate-400" data-id="element-428" data-mp="ldj9mskykped" />
                </button>
              </div>

              <div className="p-8 space-y-8" data-id="element-429" data-mp="bhq07i2jdc0t">
                <div className="text-center space-y-4" data-id="element-430" data-mp="bihjpuiuzjaq">
                  <div className="w-16 h-16 bg-amber-50 rounded-full flex items-center justify-center mx-auto text-amber-500" data-id="element-431" data-mp="7ge3ytp5psjn">
                    <AlertCircle className="w-8 h-8" data-id="element-432" data-mp="25upjbl64kocu" />
                  </div>
                  <div className="space-y-2" data-id="element-433" data-mp="21tm490vwiwco">
                    <h4 className="text-xl font-black text-slate-900" data-id="element-434" data-mp="21unj7gg1g2zo">
                      Items Still Pending
                    </h4>
                    <p className="text-sm text-slate-500 font-medium max-w-[280px] mx-auto" data-id="element-435" data-mp="3oqvcvlpq7cni">
                      Some items in this order have not been fully scanned or
                      picked.
                    </p>
                  </div>
                </div>

                <div className="space-y-3 max-h-[240px] overflow-auto pr-2 custom-scrollbar" data-id="element-436" data-mp="1czur27xbpru">
                  {items.filter(i => i.status !== 'Picked' && i.status !== 'Unavailable').map(item => <div key={item.id} className="p-4 bg-slate-50 rounded-2xl border border-slate-100 flex items-center justify-between" data-id="element-437" data-mp="39vxjcpiuch4u">
                        <div data-id="element-438" data-mp="u26ozp0cht7a">
                          <p className="text-sm font-black text-slate-900" data-id="element-439" data-mp="u1f5hck0vlxd">
                            {item.sku}
                          </p>
                          <p className="text-[10px] font-bold text-slate-400" data-id="element-440" data-mp="5ns7k074mg6b">
                            {item.name}
                          </p>
                        </div>
                        <div className="text-right" data-id="element-441" data-mp="ejrhdsdz19na">
                          <p className="text-sm font-black text-amber-600" data-id="element-442" data-mp="9qyela22cbdu">
                            {item.pickedQuantity} / {item.quantity}
                          </p>
                          <p className="text-[10px] font-black text-amber-400 uppercase tracking-widest" data-id="element-443" data-mp="oi7c05c7j11j">
                            {item.status}
                          </p>
                        </div>
                      </div>)}
                </div>

                <div className="p-4 bg-amber-50 rounded-2xl border border-amber-100" data-id="element-444" data-mp="tg8rkgafci89">
                  <p className="text-[11px] text-amber-700 font-bold leading-relaxed" data-id="element-445" data-mp="2z8xotimnhpg7">
                    <span className="font-black uppercase tracking-wider mr-1" data-id="element-446" data-mp="2v85f6o9v9ije">
                      Note:
                    </span>
                    Proceeding will mark the order as partially fulfilled. This
                    will trigger a notification to the customer and the ordering
                    channel.
                  </p>
                </div>

                <div className="flex gap-4" data-id="element-447" data-mp="2gbngfq4prcgl">
                  <button onClick={() => setShowPartialModal(false)} className="flex-1 py-4 text-slate-500 font-bold hover:bg-slate-50 rounded-2xl transition-colors" data-id="element-448" data-mp="2gam4zkjscnyc">
                    Go Back
                  </button>
                  <button type="button" onClick={handleConfirmPartial} className="flex-1 py-4 bg-amber-500 text-white font-black rounded-2xl shadow-lg shadow-amber-100 hover:bg-amber-600 transition-all" data-id="element-449" data-mp="1sf9l3al2xn5b">
                    Confirm Partial
                  </button>
                </div>
              </div>
            </motion.div>
          </div>}
      </AnimatePresence>

      {/* Customer Confirmation Wait Modal */}
      <AnimatePresence data-id="element-450" data-mp="12dgu6i1xglil">
        {isWaitingConfirmation && <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm" role="alertdialog" aria-modal="true" aria-labelledby="customer-confirmation-title" aria-describedby="customer-confirmation-description" data-id="element-451" data-mp="17c1q45a5cj18">
            <motion.div initial={{
          opacity: 0,
          scale: 0.95,
          y: 16
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.95,
          y: 16
        }} className="w-full max-w-sm overflow-hidden rounded-[28px] bg-white shadow-2xl" data-id="element-452" data-mp="1l25bku17c8pm">
              <div className="p-8 text-center" data-id="element-453" data-mp="2dxv24006ukml">
                <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-amber-50 text-amber-600" data-id="element-454" data-mp="2dzphkysoxkja">
                  <Clock className="h-8 w-8" data-id="element-455" data-mp="2551ffhiewiyp" />
                </div>
                <h3 id="customer-confirmation-title" className="mt-5 text-xl font-black text-slate-900" data-id="element-456" data-mp="2536vo6ivmww7">
                  Waiting for Customer Confirmation
                </h3>
                <p id="customer-confirmation-description" className="mt-2 text-sm leading-6 text-slate-500" data-id="element-457" data-mp="2sv59oy9pn6d1">
                  The partial fulfillment request has been sent. Choose the customer’s response to continue.
                </p>
                <div className="mt-6 grid grid-cols-2 gap-3 text-left" data-id="element-458" data-mp="1cgm0euza60nd">
                  <button onClick={() => resolveCustomerPartialConfirmation(true)} className="rounded-xl bg-emerald-600 px-3 py-3 text-center text-sm font-bold text-white transition-colors hover:bg-emerald-700" data-id="element-459" data-mp="1cej2lorvz383">
                    Customer confirmed
                  </button>
                  <button onClick={() => resolveCustomerPartialConfirmation(false)} className="rounded-xl border border-rose-200 bg-white px-3 py-3 text-center text-sm font-bold text-rose-700 transition-colors hover:bg-rose-50" data-id="element-460" data-mp="zyjepcn84gzq">
                    Customer declined
                  </button>
                </div>
              </div>
            </motion.div>
          </div>}
      </AnimatePresence>

      {/* Return Receipt Confirmation Modal */}
      <AnimatePresence data-id="element-461" data-mp="l8c7jt2o2kec">
        {showReturnReceiptModal && <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm" data-id="element-462" data-mp="l6rek7w039il">
            <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} exit={{
          opacity: 0,
          scale: 0.95
        }} className="bg-white rounded-[32px] w-full max-w-2xl overflow-hidden shadow-2xl" data-id="element-463" data-mp="c8yukqfc3dov">
              <div className="p-6 border-b border-slate-100 flex items-center justify-between bg-emerald-50/50" data-id="element-464" data-mp="3tq31y28tc0dl">
                <div className="flex items-center gap-3" data-id="element-465" data-mp="bjx4e55vldv5">
                  <div className="w-10 h-10 bg-emerald-100 rounded-xl flex items-center justify-center text-emerald-600" data-id="element-466" data-mp="6mybtr1v1ez2">
                    <CheckCircle2 className="w-6 h-6" data-id="element-467" data-mp="la9nru1yf0zp" />
                  </div>
                  <div data-id="element-468" data-mp="gbomdrayub9o">
                    <h3 className="font-bold text-slate-900" data-id="element-469" data-mp="gdj7b5m8m3dr">
                      Confirm Return Receipt
                    </h3>
                    <p className="text-[10px] font-bold text-emerald-600 uppercase tracking-widest" data-id="element-470" data-mp="u9wpkgyli8ta">
                      Verify Items from Driver
                    </p>
                  </div>
                </div>
                <button onClick={() => setShowReturnReceiptModal(false)} className="p-1 hover:bg-slate-100 rounded-full transition-colors" data-id="element-471" data-mp="2os7us39cgt2v">
                  <XCircle className="w-5 h-5 text-slate-400" data-id="element-472" data-mp="2tlafrn022u7g" />
                </button>
              </div>

              <div className="p-8 space-y-6 max-h-[70vh] overflow-y-auto custom-scrollbar" data-id="element-473" data-mp="20u8wb9pbyci4">
                <div className="space-y-4" data-id="element-474" data-mp="20wljc19y3d21">
                  {items.map(item => <div key={item.id} className={cn('p-5 rounded-2xl border-2 transition-all space-y-4', returnItemStatus[item.id]?.condition === 'Good' ? 'border-slate-100 bg-slate-50/30' : returnItemStatus[item.id]?.condition === 'Damaged' ? 'border-rose-100 bg-rose-50/30' : 'border-amber-100 bg-amber-50/30')} data-id="element-475" data-mp="25vg4vazarfpu">
                      <div className="flex items-center justify-between" data-id="element-476" data-mp="1aljiwj5xk37w">
                        <div className="flex items-center gap-4" data-id="element-477" data-mp="15t03dgnomy0g">
                          <img src={item.image} className="w-12 h-12 rounded-xl object-cover border border-slate-200" alt="" data-id="element-478" data-mp="15r6zg94gerw5" />
                          <div data-id="element-479" data-mp="qvznqbirmtic">
                            <p className="text-sm font-black text-slate-900" data-id="element-480" data-mp="quppnmbwxlbp">
                              {item.name}
                            </p>
                            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider" data-id="element-481" data-mp="hw489ui6gcci">
                              SKU: {item.sku} • Qty: {item.quantity}
                            </p>
                          </div>
                        </div>
                        <div className="flex bg-white rounded-xl p-1 border border-slate-200 shadow-sm" data-id="element-482" data-mp="2c7j2ani80xy2">
                          {(['Good', 'Damaged', 'Missing'] as const).map(cond => <button key={cond} onClick={() => setReturnItemStatus(prev => ({
                      ...prev,
                      [item.id]: {
                        ...prev[item.id],
                        condition: cond,
                        received: cond !== 'Missing'
                      }
                    }))} className={cn('px-3 py-1.5 rounded-lg text-[10px] font-black uppercase transition-all', returnItemStatus[item.id]?.condition === cond ? cond === 'Good' ? 'bg-emerald-500 text-white shadow-md shadow-emerald-100' : cond === 'Damaged' ? 'bg-rose-500 text-white shadow-md shadow-rose-100' : 'bg-amber-500 text-white shadow-md shadow-amber-100' : 'text-slate-400 hover:bg-slate-50')} data-id="element-483" data-mp="2mdslam73uh54">
                                {cond}
                              </button>)}
                        </div>
                      </div>

                      {returnItemStatus[item.id]?.condition !== 'Good' && <div className="animate-in fade-in slide-in-from-top-2 duration-200 space-y-4" data-id="element-484" data-mp="op6mgx1fgepy">
                          {returnItemStatus[item.id]?.condition === 'Missing' && <div className="space-y-2" data-id="element-485" data-mp="3vbr54nwv0rf2">
                              <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest" data-id="element-486" data-mp="36dix7hqmnu7">
                                How many units are missing?
                              </label>
                              <div className="flex items-center gap-3" data-id="element-487" data-mp="d5ckk6435j7p">
                                <button onClick={() => setReturnItemStatus(prev => ({
                        ...prev,
                        [item.id]: {
                          ...prev[item.id],
                          missingQuantity: Math.max(0, prev[item.id].missingQuantity - 1)
                        }
                      }))} className="w-10 h-10 rounded-xl border border-slate-200 flex items-center justify-center text-lg font-bold text-slate-400 hover:bg-slate-50" data-id="element-488" data-mp="i3w5q4auuqz0">
                                  -
                                </button>
                                <div className="w-16 text-center font-black text-lg text-slate-900" data-id="element-489" data-mp="2bfk1ojdxkddp">
                                  {returnItemStatus[item.id]?.missingQuantity || 0}
                                </div>
                                <button onClick={() => setReturnItemStatus(prev => ({
                        ...prev,
                        [item.id]: {
                          ...prev[item.id],
                          missingQuantity: Math.min(item.quantity, prev[item.id].missingQuantity + 1)
                        }
                      }))} className="w-10 h-10 rounded-xl border border-slate-200 flex items-center justify-center text-lg font-bold text-slate-400 hover:bg-slate-50" data-id="element-490" data-mp="2zbxuar66jola">
                                  +
                                </button>
                                <span className="text-xs text-slate-400 font-bold" data-id="element-491" data-mp="3bvlz0k18ijz2">
                                  / {item.quantity} units total
                                </span>
                              </div>
                            </div>}
                          <textarea placeholder={`Describe the ${returnItemStatus[item.id]?.condition.toLowerCase()} issue...`} value={returnItemStatus[item.id]?.note} onChange={e => setReturnItemStatus(prev => ({
                    ...prev,
                    [item.id]: {
                      ...prev[item.id],
                      note: e.target.value
                    }
                  }))} className="w-full p-3 bg-white border border-slate-200 rounded-xl text-xs outline-none focus:border-blue-500 transition-all min-h-[60px]" data-id="element-492" data-mp="3qnmdll48b4ti" />
                        </div>}
                    </div>)}
                </div>
              </div>

              <div className="p-6 bg-slate-50 border-t border-slate-100 flex gap-4" data-id="element-493" data-mp="2lerzlge89gnt">
                <button onClick={() => setShowReturnReceiptModal(false)} className="flex-1 py-4 text-slate-500 font-bold hover:bg-slate-50 rounded-2xl transition-colors" data-id="element-494" data-mp="2gobldu2l1r0e">
                  Cancel
                </button>
                <button onClick={() => {
              onUpdateStatus('Return Requested');
              setShowReturnReceiptModal(false);
              import('sonner').then(({
                toast
              }) => toast.success('Return receipt confirmed. Status updated to Return Requested.'));
            }} className="flex-[2] py-4 bg-emerald-600 text-white font-black rounded-2xl shadow-lg shadow-emerald-100 hover:bg-emerald-700 transition-all" data-id="element-495" data-mp="jp2v1xk7uyl5">
                  Confirm Receipt & Proceed
                </button>
              </div>
            </motion.div>
          </div>}
      </AnimatePresence>

      {/* Return Modal */}
      <AnimatePresence data-id="element-496" data-mp="1ldwz9ujfng59">
        {showReturnModal && order.channel !== 'Aggregator' && <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm" data-id="element-497" data-mp="35ybpgdy4ilbg">
            <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} exit={{
          opacity: 0,
          scale: 0.95
        }} className="bg-white rounded-3xl w-full max-w-2xl overflow-hidden shadow-2xl" data-id="element-498" data-mp="30zqogcb1rxly">
              <div className="p-6 border-b border-slate-100 flex items-center justify-between bg-rose-50/50" data-id="element-499" data-mp="3jr1fqukoqced">
                <div className="flex items-center gap-3" data-id="element-500" data-mp="3tmv0h18k5c78">
                  <ArrowLeft className="w-6 h-6 text-rose-600" data-id="element-501" data-mp="3tne8nq8eom82" />
                  <h3 className="font-bold text-slate-900" data-id="element-502" data-mp="247evly1o2cn5">Return Process</h3>
                </div>
                <button onClick={() => setShowReturnModal(false)} className="p-1 hover:bg-slate-100 rounded-full" data-id="element-503" data-mp="1z9ddbfc2ehcq">
                  <XCircle className="w-5 h-5 text-slate-400" data-id="element-504" data-mp="qe9i1gagzft9" />
                </button>
              </div>
              <div className="p-8 space-y-6" data-id="element-505" data-mp="10a2y39nedrfs">
                <div className="grid grid-cols-2 gap-6" data-id="element-506" data-mp="10be29akr917f">
                  <div className="space-y-2" data-id="element-507" data-mp="14bei8zni5kgl">
                    <label className="text-xs font-bold text-slate-500 uppercase" data-id="element-508" data-mp="14b4p5375ux6o">
                      Return Reason
                    </label>
                    <select value={returnReason} onChange={event => {
                  setReturnReason(event.target.value);
                  if (!returnReasons.find(reason => reason.label === event.target.value)?.requiresFreeText) setReturnCustomReason('');
                }} className="w-full px-4 py-3 bg-slate-50 border-2 border-slate-100 rounded-xl text-sm outline-none" data-id="element-509" data-mp="cki6k2d6hluw">
                      <option value="" data-id="element-510" data-mp="3ozj0zim8tf9b">Select a reason...</option>
                      {returnReasons.map(reason => <option key={reason.id} value={reason.label} data-id="element-511" data-mp="3hg09sp2uifpn">{reason.label}</option>)}
                    </select>
                    {returnReasonRequiresText && <textarea value={returnCustomReason} onChange={event => setReturnCustomReason(event.target.value)} placeholder="Enter the return reason..." className="min-h-[76px] w-full rounded-xl border-2 border-slate-100 bg-slate-50 p-3 text-sm outline-none transition-colors focus:border-rose-500 focus:bg-white" data-id="element-512" data-mp="38g4x2ftn6cni" />}
                  </div>
                  <div className="space-y-2" data-id="element-513" data-mp="2ktexdl4rmu62">
                    <label className="text-xs font-bold text-slate-500 uppercase" data-id="element-514" data-mp="2ft0uehi0uaew">
                      Return Method
                    </label>
                    <div className="grid grid-cols-2 gap-2" data-id="element-515" data-mp="2av8rytjr35wg">
                      <button onClick={() => setReturnMethod('Driver')} className={cn('py-3 rounded-xl border-2 text-xs font-bold transition-all', returnMethod === 'Driver' ? 'border-blue-600 bg-blue-50 text-blue-600' : 'border-slate-100 text-slate-500 hover:border-slate-200')} data-id="element-516" data-mp="216lhrudipo9z">
                        Request Driver
                      </button>
                      <button onClick={() => setReturnMethod('Customer')} className={cn('py-3 rounded-xl border-2 text-xs font-bold transition-all', returnMethod === 'Customer' ? 'border-blue-600 bg-blue-50 text-blue-600' : 'border-slate-100 text-slate-500 hover:border-slate-200')} data-id="element-517" data-mp="2ov4lrxpjbew9">
                        Customer Return
                      </button>
                    </div>
                  </div>
                </div>

                <div className="space-y-4" data-id="element-518" data-mp="15x8becmbwo2w">
                  {items.map(item => <div key={item.id} className="p-4 bg-slate-50 rounded-xl flex items-center justify-between" data-id="element-519" data-mp="15z19h3ivlzza">
                      <div className="flex items-center gap-3" data-id="element-520" data-mp="d8t0a16bipwu">
                        <img src={item.image} className="w-10 h-10 rounded-lg" data-id="element-521" data-mp="8ahrzuvo8ngq" />
                        <p className="text-sm font-bold" data-id="element-522" data-mp="i7nhiyon06eu">{item.name}</p>
                      </div>
                      <div className="flex items-center gap-3" data-id="element-523" data-mp="1xoe6gen6zvlb">
                        <button className="w-8 h-8 rounded border border-slate-200" data-id="element-524" data-mp="1xmtcaq4sienz">
                          -
                        </button>
                        <span className="text-sm font-bold" data-id="element-525" data-mp="3n5ru9qkr9pug">0</span>
                        <button className="w-8 h-8 rounded border border-slate-200" data-id="element-526" data-mp="3n3f4ir8rab6u">
                          +
                        </button>
                        <span className="text-xs text-slate-400" data-id="element-527" data-mp="xs1w1ub0tjr">
                          / {item.quantity}
                        </span>
                      </div>
                    </div>)}
                </div>
                <button disabled={!returnReason || returnReasonRequiresText && !returnCustomReason.trim()} onClick={() => {
              const nextStatus = returnMethod === 'Driver' ? 'Waiting Return Confirmation' : 'Return Requested';
              onUpdateStatus(nextStatus, {
                returnReason: returnReasonRequiresText ? returnCustomReason.trim() : returnReason
              });
              setReturnCustomReason('');
              setShowReturnModal(false);
              import('sonner').then(({
                toast
              }) => toast.success(returnMethod === 'Driver' ? 'Driver requested for return. Waiting for item arrival.' : 'Return initiated. Please scan items when received.'));
            }} className="w-full py-4 bg-rose-600 text-white font-bold rounded-2xl shadow-lg disabled:opacity-50" data-id="element-528" data-mp="9q3m35ytrkra">
                  Confirm Return ({returnMethod})
                </button>
              </div>
            </motion.div>
          </div>}
      </AnimatePresence>

      {/* Rx Modal */}
      <AnimatePresence data-id="element-529" data-mp="1ci66b9tcwaj0">
        {showRxModal && <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm" data-id="element-530" data-mp="1cllpanq5i3pp">
            <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} exit={{
          opacity: 0,
          scale: 0.95
        }} className="bg-white rounded-3xl w-full max-w-2xl overflow-hidden shadow-2xl" data-id="element-531" data-mp="14c2psbq6vk6">
              <div className="p-6 border-b border-slate-100 flex items-center justify-between bg-purple-50/50" data-id="element-532" data-mp="2zj5oiyg9s1d6">
                <h3 className="font-bold text-slate-900" data-id="element-533" data-mp="3e70ew86t1tn3">
                  Prescription Attachment
                </h3>
                <button onClick={() => setShowRxModal(false)} className="p-1 hover:bg-slate-100 rounded-full" data-id="element-534" data-mp="398yy5twuq6es">
                  <XCircle className="w-5 h-5 text-slate-400" data-id="element-535" data-mp="3j8g1i4oms87l" />
                </button>
              </div>
              <div className="p-8" data-id="element-536" data-mp="1sw0ed1tvptg4">
                <div className="aspect-[4/3] bg-slate-100 rounded-2xl border-2 border-slate-200 overflow-hidden" data-id="element-537" data-mp="1sx1pf6awafxu">
                  {order.prescription?.image ? <img src={order.prescription.image} alt="Electronic prescription" className="w-full h-full object-contain" data-id="element-538" data-mp="1eqq7h67nuhf7" /> : <div className="flex flex-col items-center justify-center h-full text-slate-400" data-id="element-539" data-mp="14xswrm8wtwr5">
                      <Globe className="w-12 h-12 mb-2" data-id="element-540" data-mp="19wdqd2tnpi6m" />
                      <p className="font-bold" data-id="element-541" data-mp="v0obdomw25be">No Image Attached</p>
                    </div>}
                </div>
                <div className="mt-6 flex gap-3" data-id="element-542" data-mp="zy690ivggy43">
                  <button onClick={() => setShowRxModal(false)} className="flex-1 rounded-2xl border border-slate-200 bg-white py-4 font-bold text-slate-600" data-id="element-543" data-mp="m1t4eac0sr71">
                    Close
                  </button>
                  {order.prescription && !order.prescription.verified && <button onClick={() => {
                onUpdateStatus(order.status, {
                  prescription: {
                    ...order.prescription,
                    verified: true
                  }
                });
                setShowRxModal(false);
              }} className="flex flex-1 items-center justify-center gap-2 rounded-2xl bg-emerald-600 py-4 font-bold text-white shadow-lg shadow-emerald-100 transition-colors hover:bg-emerald-700" data-id="element-544" data-mp="2lbj4r435h562">
                      <CheckCircle2 className="h-4 w-4" data-id="element-545" data-mp="2xnl60km10hps" />
                      Approve eRx
                    </button>}
                  <button onClick={() => {
                setShowRxModal(false);
                setShowHoldModal(true);
                setHoldReason('Prescription Not Valid');
              }} className="flex flex-1 items-center justify-center gap-2 rounded-2xl bg-amber-500 py-4 font-bold text-white shadow-lg shadow-amber-100 transition-colors hover:bg-amber-600" data-id="element-546" data-mp="2svtpl2wl3sdd">
                    <Clock className="h-4 w-4" data-id="element-547" data-mp="3swyi8izwcc4s" />
                    Hold eRx
                  </button>
                </div>
              </div>
            </motion.div>
          </div>}
      </AnimatePresence>

      {/* Hold Modal */}
      <AnimatePresence data-id="element-548" data-mp="6qrszzkunhxc">
        {showHoldModal && <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm" data-id="element-549" data-mp="arva0pemvk05">
            <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} exit={{
          opacity: 0,
          scale: 0.95
        }} className="bg-white rounded-3xl w-full max-w-md overflow-hidden shadow-2xl" data-id="element-550" data-mp="fq6ml7wbc8m2">
              <div className="p-6 border-b border-slate-100 flex items-center justify-between" data-id="element-551" data-mp="yhzd8vnwthqm">
                <h3 className="font-bold text-slate-900" data-id="element-552" data-mp="1w51108yvfbtn">Hold Order</h3>
                <button onClick={() => setShowHoldModal(false)} className="p-1 hover:bg-slate-100 rounded-full" data-id="element-553" data-mp="1r7hl74miyqzl">
                  <XCircle className="w-5 h-5 text-slate-400" data-id="element-554" data-mp="250h1v9y1mr8q" />
                </button>
              </div>
              <div className="p-8 space-y-6" data-id="element-555" data-mp="20z57ys84zjuq">
                <div className="space-y-2" data-id="element-556" data-mp="1d882nyaujksg">
                  {holdReasons.map(reason => <button key={reason.id} onClick={() => {
                setHoldReason(reason.label);
                if (!reason.requiresFreeText) setHoldCustomReason('');
              }} className={cn('w-full px-4 py-3 rounded-xl border-2 text-left text-sm font-medium transition-all', holdReason === reason.label ? 'border-slate-800 bg-slate-800 text-white' : 'border-slate-100 bg-slate-50 text-slate-600 hover:border-slate-200')} data-id="element-557" data-mp="1895b9gnshd2y">
                      {reason.label}
                    </button>)}
                  {holdReasonRequiresText && <textarea value={holdCustomReason} onChange={event => setHoldCustomReason(event.target.value)} placeholder="Enter the hold reason..." className="min-h-[88px] w-full rounded-xl border-2 border-slate-100 bg-slate-50 p-4 text-sm outline-none transition-colors focus:border-slate-800 focus:bg-white" data-id="element-558" data-mp="2w6nvr2bj140w" />}
                </div>
                <button disabled={!holdReason || holdReasonRequiresText && !holdCustomReason.trim()} onClick={() => {
              onUpdateStatus('Held', {
                holdReason: holdReasonRequiresText ? holdCustomReason.trim() : holdReason
              });
              setHoldCustomReason('');
              setShowHoldModal(false);
            }} className="w-full py-4 bg-slate-800 text-white font-bold rounded-2xl shadow-lg disabled:opacity-50" data-id="element-559" data-mp="6of6203it173">
                  Confirm Hold
                </button>
              </div>
            </motion.div>
          </div>}
      </AnimatePresence>

      {/* Patient Share Collection Modal */}
      <AnimatePresence data-id="element-560" data-mp="3tpcp6kibguti">
        {showPatientShareModal && order.insurance && <div className="fixed inset-0 z-[70] flex items-center justify-center bg-slate-900/60 p-4 backdrop-blur-sm" data-id="element-561" data-mp="3osw0np77gefw">
            <motion.div initial={{
          opacity: 0,
          scale: 0.95,
          y: 16
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.95,
          y: 16
        }} className="w-full max-w-md overflow-hidden rounded-3xl bg-white shadow-2xl" role="dialog" aria-modal="true" aria-labelledby="patient-share-title" data-id="element-562" data-mp="3jr6q0anwa2sc">
              <div className="flex items-center justify-between border-b border-violet-100 bg-violet-50/70 p-6" data-id="element-563" data-mp="1vbgmh09cxd6s">
                <div className="flex items-center gap-3" data-id="element-564" data-mp="24a20qj7sq61r">
                  <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-violet-100 text-violet-700" data-id="element-565" data-mp="249iu3ynlj431"><CreditCard className="h-5 w-5" data-id="element-566" data-mp="17lw2ce1gw9vx" /></div>
                  <div data-id="element-567" data-mp="1bkv3dnk2vmi3">
                    <h3 id="patient-share-title" className="font-bold text-slate-900" data-id="element-568" data-mp="1bmfz3gi4pbhj">Collect Patient Share</h3>
                    <p className="text-[10px] font-bold uppercase tracking-widest text-violet-700" data-id="element-569" data-mp="1gjeqhraer4c1">Insurance walk-in</p>
                  </div>
                </div>
                <button onClick={() => setShowPatientShareModal(false)} className="rounded-full p-1 text-slate-400 transition-colors hover:bg-white" aria-label="Close patient share collection dialog" data-id="element-570" data-mp="nwtxyni74m6w"><XCircle className="h-5 w-5" data-id="element-571" data-mp="3icyvgf9ehnkd" /></button>
              </div>
              <div className="space-y-5 p-8" data-id="element-572" data-mp="3if1qxetbo8wh">
                <div className="rounded-2xl border border-violet-100 bg-violet-50/60 p-5 text-center" data-id="element-573" data-mp="3df5ohk09wpj1">
                  <p className="text-[10px] font-black uppercase tracking-widest text-violet-600" data-id="element-574" data-mp="3reyd99s8ovod">Amount due from patient</p>
                  <p className="mt-2 text-3xl font-black text-slate-900" data-id="element-575" data-mp="3nb0bssffz8s5">{order.insurance.patientShare.toFixed(2)} SAR</p>
                  <p className="mt-2 text-xs text-slate-500" data-id="element-576" data-mp="54c7h4s7upnj">{order.insurance.payerName} covers {order.insurance.insuranceShare.toFixed(2)} SAR · {order.insurance.copayPercent}% copay</p>
                </div>
                <p className="text-center text-sm leading-6 text-slate-600" data-id="element-577" data-mp="9xf09ap65v2o">Confirm collection at the counter to release this order for picking.</p>
              </div>
              <div className="flex gap-3 border-t border-slate-100 bg-slate-50 p-6" data-id="element-578" data-mp="nxg1ir9cj4io">
                <button onClick={() => setShowPatientShareModal(false)} className="flex-1 rounded-xl py-3.5 font-bold text-slate-600 transition-colors hover:bg-white" data-id="element-579" data-mp="izocq63zzyip">Cancel</button>
                <button onClick={() => {
              onUpdateStatus('Picking', {
                insurance: {
                  ...order.insurance,
                  paymentLinkStatus: 'Paid'
                }
              });
              setShowPatientShareModal(false);
            }} className="flex-1 rounded-xl bg-emerald-600 py-3.5 font-bold text-white shadow-lg shadow-emerald-100 transition-colors hover:bg-emerald-700" data-id="element-580" data-mp="17hx5emlfiwa5">
                  Collect & Start Picking
                </button>
              </div>
            </motion.div>
          </div>}
      </AnimatePresence>

      {/* Yaser Label Preview */}
      <AnimatePresence data-id="element-581" data-mp="ev2ilj153kti">
        {showLabelModal && scanningItem && fulfillmentProfile.requiresPrescriptionReview && <div className="fixed inset-0 z-[70] flex items-center justify-center bg-slate-900/60 p-4 backdrop-blur-sm" data-id="element-582" data-mp="wmg64ab8gh6">
            <motion.div initial={{
          opacity: 0,
          scale: 0.95,
          y: 16
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.95,
          y: 16
        }} className="w-full max-w-sm overflow-hidden rounded-3xl bg-white shadow-2xl" role="dialog" aria-modal="true" aria-labelledby="label-preview-title" data-id="element-583" data-mp="40pp6kpy1rtl">
              <div className="flex items-center justify-between border-b border-purple-100 bg-purple-50/70 p-6" data-id="element-584" data-mp="3w4iiefyegkip"><div data-id="element-585" data-mp="nop0pxsc1302"><h3 id="label-preview-title" className="font-bold text-slate-900" data-id="element-586" data-mp="noozjugliwyh">Dispensing label</h3><p className="text-[10px] font-bold uppercase tracking-widest text-purple-700" data-id="element-587" data-mp="nr2um3ba0961">Yaser eRx</p></div><button onClick={() => setShowLabelModal(false)} className="rounded-full p-1 text-slate-400 hover:bg-white" aria-label="Close label preview" data-id="element-588" data-mp="rs4veokucta4"><XCircle className="h-5 w-5" data-id="element-589" data-mp="ix6zr9qb72f3" /></button></div>
              <div className="m-6 space-y-4 rounded-2xl border-2 border-dashed border-slate-200 p-5 text-sm text-slate-700" data-id="element-590" data-mp="ivnhjdl3ehao"><p className="font-black text-slate-900" data-id="element-591" data-mp="1beaxyfp5985f">{scanningItem.dispensedTradeDrugCode || scanningItem.tradeDrugCode}</p><p data-id="element-592" data-mp="1ksuyaur7aba2">{scanningItem.name}</p><p className="font-mono text-xs" data-id="element-593" data-mp="1ks1m3e9h2tkj">Lot: {scanningItem.selectedLotNumber || 'Select during picking'}</p><p className="border-t border-slate-100 pt-3 text-xs leading-5" data-id="element-594" data-mp="1gqgea7bsp5h7">{scanningItem.instructionsAr || 'Use as directed by your pharmacist.'}</p><p className="text-[10px] font-bold uppercase text-slate-400" data-id="element-595" data-mp="1ui4scg5lu40t">Patient: {order.customer.name}</p></div>
              <div className="flex gap-3 border-t border-slate-100 bg-slate-50 p-6" data-id="element-596" data-mp="1pm5wk7hy5jqd"><button onClick={() => setShowLabelModal(false)} className="flex-1 rounded-xl py-3.5 font-bold text-slate-600 hover:bg-white" data-id="element-597" data-mp="11uqxqopdpkjv">Close</button><button onClick={() => window.print()} className="flex-1 rounded-xl bg-purple-600 py-3.5 font-bold text-white shadow-lg shadow-purple-100 transition-colors hover:bg-purple-700" data-id="element-598" data-mp="1bsewdpq3vc7b">Print label</button></div>
            </motion.div>
          </div>}
      </AnimatePresence>

      {/* OTP Modal */}
      <AnimatePresence data-id="element-599" data-mp="2i90ccr3tjuxi">
        {showOtpModal && <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm" data-id="element-600" data-mp="2i88t8frerpo4">
            <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} exit={{
          opacity: 0,
          scale: 0.95
        }} className="bg-white rounded-3xl w-full max-w-md overflow-hidden shadow-2xl" data-id="element-601" data-mp="2qbj20cn0l0zt">
              <div className="flex items-center justify-between border-b border-slate-100 p-6" data-id="element-602" data-mp="2bk0qar16tsm9">
                <h3 className="font-bold text-slate-900" data-id="element-603" data-mp="2gjn534wnzgx6">{fulfillmentProfile.requiresNationalIdVerification ? 'National ID Verification' : 'OTP Verification'}</h3>
                <button onClick={() => setShowOtpModal(false)} className="rounded-full p-1 transition-colors hover:bg-slate-100" aria-label="Close pickup verification dialog" data-id="element-604" data-mp="2lbemaotxkaan">
                  <XCircle className="h-5 w-5 text-slate-400" data-id="element-605" data-mp="1sshogniwgklt" />
                </button>
              </div>
              <div className="space-y-6 p-8" data-id="element-606" data-mp="1l9rmvi6bxtx2">
                <div className="rounded-2xl border border-purple-100 bg-purple-50 p-4 text-center" data-id="element-607" data-mp="1gbgc9587nfds">
                  <div className="mx-auto mb-2 flex h-10 w-10 items-center justify-center rounded-xl bg-white text-purple-600 shadow-sm" data-id="element-608" data-mp="1bbtz0vsdtz4z"><User className="h-5 w-5" data-id="element-609" data-mp="16dshwgeio9w5" /></div>
                  <p className="text-[10px] font-black uppercase tracking-widest text-purple-600" data-id="element-610" data-mp="16f3jc9khp5k3">{fulfillmentProfile.requiresNationalIdVerification ? 'Yaser eRx Pickup' : 'Self Pickup'}</p>
                  <p className="mt-1 text-sm font-bold text-slate-900" data-id="element-611" data-mp="xje4ykhglbg7">{order.customer.name} is collecting this order</p>
                </div>

                {fulfillmentProfile.requiresNationalIdVerification ? <>
                    <div className="space-y-2 text-center" data-id="element-612" data-mp="in6kdj25280e">
                      <ShieldCheck className="mx-auto h-8 w-8 text-purple-600" data-id="element-613" data-mp="imn56a3vhxq2" />
                      <h4 className="text-lg font-bold" data-id="element-614" data-mp="emcx8rwisx8k">Verify National ID</h4>
                      <p className="text-sm text-slate-500" data-id="element-615" data-mp="13dijkup9fe06">Match the patient’s national ID before releasing this Yaser eRx order.</p>
                    </div>
                    <input autoFocus inputMode="numeric" type="text" value={nationalIdValue} onChange={event => setNationalIdValue(event.target.value.replace(/\D/g, ''))} placeholder="National ID" aria-label="Patient national ID" className="w-full rounded-2xl border-2 border-slate-100 bg-slate-50 px-5 py-4 text-center font-mono text-xl font-black tracking-wider outline-none transition-colors focus:border-purple-500 focus:bg-white" data-id="element-616" data-mp="uk5ih5h1l630" />
                    <div className="space-y-2" data-id="element-617" data-mp="25z2mblhvxzuf">
                      <label className="text-xs font-bold uppercase tracking-wider text-slate-500" data-id="element-618" data-mp="25x7wptvo5jk0">Patient mobile (optional)</label>
                      <input inputMode="tel" type="tel" value={pickupMobileValue} onChange={event => setPickupMobileValue(event.target.value.replace(/\D/g, ''))} placeholder={order.customer.mobile} aria-label="Optional patient mobile number" className="w-full rounded-xl border-2 border-slate-100 bg-slate-50 px-4 py-3 text-sm font-medium outline-none transition-colors focus:border-purple-500 focus:bg-white" data-id="element-619" data-mp="3uioubhefk739" />
                    </div>
                    <button disabled={nationalIdValue !== order.customer.nationalId} onClick={() => {
                onUpdateStatus('Delivered', {
                  customer: {
                    ...order.customer,
                    mobile: pickupMobileValue || order.customer.mobile
                  }
                });
                setShowOtpModal(false);
              }} className="w-full rounded-2xl bg-purple-600 py-4 font-bold text-white shadow-lg shadow-purple-100 transition-colors hover:bg-purple-700 disabled:cursor-not-allowed disabled:opacity-50" data-id="element-620" data-mp="r16mh9ynr02u">
                      Verify ID & Complete
                    </button>
                  </> : <>
                    <div className="space-y-2 text-center" data-id="element-621" data-mp="1aqr7q7iivmzo">
                      <Smartphone className="mx-auto h-8 w-8 text-blue-600" data-id="element-622" data-mp="1ao4x1z0c4rd8" />
                      <h4 className="text-lg font-bold" data-id="element-623" data-mp="1fnrdsifdgpqs">Confirm Pickup with OTP</h4>
                      <p className="text-sm text-slate-500" data-id="element-624" data-mp="1khdh3jznbh9x">Ask the customer for the 4-digit collection code sent to {order.customer.mobile}.</p>
                    </div>
                    <input autoFocus inputMode="numeric" type="text" maxLength={4} value={otpValue} onChange={event => setOtpValue(event.target.value.replace(/\D/g, ''))} aria-label="Four digit pickup OTP" className="w-full rounded-2xl border-2 border-slate-100 bg-slate-50 py-6 text-center text-4xl font-black tracking-widest outline-none transition-colors focus:border-blue-500 focus:bg-white" data-id="element-625" data-mp="1th8wk108hyfr" />
                    <div className="flex items-center justify-center gap-4 text-xs font-bold" data-id="element-626" data-mp="5jhkrifmmzn3">
                      <button onClick={handleSendWhatsApp} className="text-blue-600 transition-colors hover:text-blue-800" data-id="element-627" data-mp="1hmmynwwn4e2">Send via WhatsApp</button>
                      <span className="h-3 border-l border-slate-200" data-id="element-628" data-mp="3suh98psm6k04" />
                      <button disabled={!canResend} onClick={handleResendOtp} className="text-blue-600 transition-colors hover:text-blue-800 disabled:cursor-not-allowed disabled:text-slate-400" data-id="element-629" data-mp="3nvw7glxprs9k">
                        {canResend ? 'Resend OTP' : `Resend in ${resendTimer}s`}
                      </button>
                    </div>
                    <button disabled={otpValue.length !== 4} onClick={() => {
                if (otpValue === order.otp) {
                  onUpdateStatus('Delivered', {
                    otpVerified: true
                  });
                  setShowOtpModal(false);
                } else {
                  import('sonner').then(({
                    toast
                  }) => toast.error('Invalid OTP'));
                }
              }} className="w-full rounded-2xl bg-blue-600 py-4 font-bold text-white shadow-lg shadow-blue-100 transition-colors hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50" data-id="element-630" data-mp="3owqn3om5izpk">
                      Verify & Complete
                    </button>
                  </>}
              </div>
            </motion.div>
          </div>}
      </AnimatePresence>
    </div>;
}